/**
 * component-my-assortment.js
 * ──────────────────────────────────────────────────────────────────────────
 * Fully JSON-driven Assortment page.
 *
 * Unlike the legacy `assortment-products` element (which fetched Shopify Search
 * API HTML + Section Rendering API and cached HTML in IndexedDB), this element
 * builds every product card in the browser from JSON returned by
 *   POST /apps/stroetmann/api/v1/users/get_assortment_details
 * and caches the JSON (never HTML) in IndexedDB.
 *
 * DESIGN GOALS (per spec)
 *   • No global variables — everything lives inside this IIFE / the class.
 *   • Category + custom-tag filters render INSTANTLY from the metafield JSON
 *     embedded in the section (zero network wait).
 *   • Initial load fetches only the first 100 products.
 *   • Infinite scroll appends the next 100 as the footer approaches.
 *   • Applying a filter gathers the matching KIOS ids from the metafield JSON
 *     into an array and passes it as `kioIds` to `get_assortment_details`.
 *   • JSON is cached per product id in IndexedDB and flushed on the SAME
 *     morning/evening schedule as assets/assortment-cache.js.
 *
 * The custom element is registered as <my-assortment>. Product-card interaction
 * (add-to-KIOS heart, custom-tag toggles) is delegated to the existing
 * <assortment-interact> element (assets/component-assortment.js), which the
 * generated markup upgrades automatically.
 * ──────────────────────────────────────────────────────────────────────────
 */
(() => {
  'use strict';

  if (customElements.get('my-assortment')) return;

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSTANTS
  // ═══════════════════════════════════════════════════════════════════════════
  const API = {
    SET_ASSORTMENT: '/apps/stroetmann/api/v1/customers/set_assortment',
    GET_DETAILS: '/apps/stroetmann/api/v1/users/get_assortment_details',
    SET_ASSORTMENT_SORT: '/apps/stroetmann/api/v1/users/set_assortment_sort',
    CREATE_KIO: '/apps/stroetmann/api/v1/users/create_kio',
    DELETE_KIO: '/apps/stroetmann/api/v1/users/delete_kio',
  };
  /** Products fetched + rendered per chunk (initial load and each scroll). */
  const PAGE_SIZE = 100;
  /** Distance from the bottom (px) at which the next chunk starts loading. */
  const INFINITE_SCROLL_OFFSET = 800;
  /** Placeholder cards drawn in the grid while a page/filter is loading. */
  const SKELETON_CARD_COUNT = 6;

  /**
   * GLOBAL CACHE KILL-SWITCH.
   * Set `window.MY_ASSORTMENT_DISABLE_CACHE = true` (before this script runs, or
   * at any time in the console) to bypass IndexedDB entirely — every chunk is
   * fetched fresh from the API and nothing is written to the cache. This is the
   * one intentional global in this module.
   */
  window.MY_ASSORTMENT_DISABLE_CACHE = window.MY_ASSORTMENT_DISABLE_CACHE ?? false;
  const cacheDisabled = () => window.MY_ASSORTMENT_DISABLE_CACHE === true;

  // ═══════════════════════════════════════════════════════════════════════════
  // IDB JSON CACHE  (schedule identical to assets/assortment-cache.js)
  // Stores { key:"product:<id>", data:<productJSON>, storedAt:<ms> }.
  // ═══════════════════════════════════════════════════════════════════════════
  const JsonCache = (() => {
    const DB_NAME = 'my-assortment-cache';
    const DB_VERSION = 1;              // records indexed by kioId + handle (+ productId key)
    const STORE_NAME = 'products';
    const TIMEZONE = 'Europe/Berlin';

    // Keep these two windows in sync with assets/assortment-cache.js SCHEDULE.
    const SCHEDULE = {
      morning: { enabled: true, hour: 7, minute: 30 },
      evening: { enabled: true, hour: 10, minute: 30 },
    };
    const FALLBACK_TTL_MS = 24 * 60 * 60 * 1000;

    let _db = null;
    let _openPromise = null;

    function _toBerlinParts(ts) {
      const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
      const parts = fmt.formatToParts(new Date(ts));
      const get = t => parseInt(parts.find(p => p.type === t)?.value ?? '0', 10);
      return {
        year: get('year'), month: get('month'), day: get('day'),
        hour: get('hour'), minute: get('minute'), second: get('second'),
      };
    }

    function _berlinToUTC(year, month, day, hour, minute) {
      const p = n => String(n).padStart(2, '0');
      for (const offset of [2, 1]) {
        const iso = `${year}-${p(month)}-${p(day)}T${p(hour)}:${p(minute)}:00+0${offset}:00`;
        const candidate = new Date(iso).getTime();
        const c = _toBerlinParts(candidate);
        if (c.year === year && c.month === month && c.day === day && c.hour === hour && c.minute === minute) {
          return candidate;
        }
      }
      return new Date(`${year}-${p(month)}-${p(day)}T${p(hour)}:${p(minute)}:00+01:00`).getTime();
    }

    function _flushPointsForDay(y, m, d) {
      const pts = [];
      for (const w of Object.values(SCHEDULE)) {
        if (w.enabled) pts.push(_berlinToUTC(y, m, d, w.hour, w.minute));
      }
      return pts.sort((a, b) => a - b);
    }

    function _lastFlushPointBefore(now) {
      if (!Object.values(SCHEDULE).some(w => w.enabled)) return null;
      const berlin = _toBerlinParts(now);
      const candidates = [];
      for (let back = 0; back <= 2; back++) {
        const dayTs = _berlinToUTC(berlin.year, berlin.month, berlin.day, 0, 0) - back * 86_400_000;
        const dp = _toBerlinParts(dayTs);
        for (const pt of _flushPointsForDay(dp.year, dp.month, dp.day)) {
          if (pt <= now) candidates.push(pt);
        }
      }
      return candidates.length ? Math.max(...candidates) : null;
    }

    function _isExpired(storedAt, now = Date.now()) {
      const lastFlush = _lastFlushPointBefore(now);
      if (lastFlush === null) return (now - storedAt) > FALLBACK_TTL_MS;
      return storedAt <= lastFlush;
    }

    function _open() {
      if (_db) return Promise.resolve(_db);
      if (_openPromise) return _openPromise;
      _openPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = e => {
          const db = e.target.result;
          const store = db.objectStoreNames.contains(STORE_NAME)
            ? e.target.transaction.objectStore(STORE_NAME)
            : db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          // A product record can be located three ways: by product-id key, and
          // via the kioId / handle indexes (so filters can cache-check with the
          // exact key they would otherwise send to the API).
          if (!store.indexNames.contains('storedAt')) store.createIndex('storedAt', 'storedAt', { unique: false });
          if (!store.indexNames.contains('kioId')) store.createIndex('kioId', 'kioId', { unique: false });
          if (!store.indexNames.contains('handle')) store.createIndex('handle', 'handle', { unique: false });
        };
        req.onsuccess = e => {
          _db = e.target.result;
          _db.onclose = () => { _db = null; _openPromise = null; };
          resolve(_db);
        };
        req.onerror = () => reject(req.error);
        req.onblocked = () => reject(new Error('[MyAssortment] IDB open blocked'));
      });
      return _openPromise;
    }

    const _key = id => `product:${id}`;

    /**
     * Shared point-lookup: fetch each `values` entry through the object store
     * (`by='key'`) or an index (`by='kioId'|'handle'`), skipping expired records.
     * @returns {Promise<Map<string, object>>} lookupValue(String) → product data
     */
    async function _getBy(by, values) {
      const result = new Map();
      const list = [...new Set((values ?? []).filter(v => v != null).map(String))];
      if (!list.length) return result;
      try {
        const db = await _open();
        await new Promise(resolve => {
          const tx = db.transaction(STORE_NAME, 'readonly');
          const store = tx.objectStore(STORE_NAME);
          const source = by === 'key' ? store : store.index(by);
          let pending = list.length;
          const done = () => { if (--pending === 0) resolve(); };
          for (const v of list) {
            const req = source.get(by === 'key' ? _key(v) : v);
            req.onsuccess = () => {
              const rec = req.result;
              if (rec && !_isExpired(rec.storedAt)) result.set(v, rec.data);
              done();
            };
            req.onerror = () => done();
          }
        });
      } catch (e) { /* return whatever collected */ }
      return result;
    }

    /** Cache lookup by product id → Map<productId, data>. */
    const getByProductIds = ids => _getBy('key', ids);
    /** Cache lookup by KIO id → Map<kioId, data> (category filters). */
    const getByKioIds = kioIds => _getBy('kioId', kioIds);
    /** Cache lookup by product handle → Map<handle, data> (custom-tag filters). */
    const getByHandles = handles => _getBy('handle', handles);

    /**
     * Write products in one transaction. Each entry stores the product JSON plus
     * its kioId + handle so the record is findable by all three keys.
     * @param {Array<{productId:string|number, kioId?:*, handle?:string, data:object}>} entries
     */
    async function setMany(entries) {
      if (!entries.length) return;
      try {
        const db = await _open();
        const now = Date.now();
        await new Promise((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
          const store = tx.objectStore(STORE_NAME);
          for (const { productId, kioId, handle, data } of entries) {
            const rec = { key: _key(productId), productId: String(productId), data, storedAt: now };
            if (kioId != null) rec.kioId = String(kioId);   // omitted keys stay out of the index
            if (handle) rec.handle = String(handle);
            store.put(rec);
          }
        });
      } catch (e) { console.warn('[MyAssortment] cache setMany failed:', e); }
    }

    async function del(id) {
      try {
        const db = await _open();
        await new Promise((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
          tx.objectStore(STORE_NAME).delete(_key(id));
        });
      } catch (e) { /* non-fatal */ }
    }

    /** Range-delete everything stored before the most recent flush point. */
    async function evictExpired() {
      try {
        const db = await _open();
        const now = Date.now();
        const lastFlush = _lastFlushPointBefore(now);
        const boundary = lastFlush !== null ? lastFlush : now - FALLBACK_TTL_MS;
        if (boundary <= 0) return 0;
        return await new Promise((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          const range = IDBKeyRange.upperBound(boundary, false);
          const req = tx.objectStore(STORE_NAME).index('storedAt').openCursor(range);
          let n = 0;
          req.onsuccess = e => {
            const cursor = e.target.result;
            if (!cursor) return resolve(n);
            cursor.delete(); n++; cursor.continue();
          };
          req.onerror = () => reject(req.error);
        });
      } catch (e) { return 0; }
    }

    return { getByProductIds, getByKioIds, getByHandles, setMany, del, evictExpired, _isExpired };
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  // PURE HELPERS
  // ═══════════════════════════════════════════════════════════════════════════
  const stripProductGid = v => String(v ?? '').replace('gid://shopify/Product/', '');
  const stripVariantGid = v => String(v ?? '').replace('gid://shopify/ProductVariant/', '');

  function escAttr(str) { return String(str ?? '').replace(/"/g, '&quot;'); }
  function escHTML(str) {
    const d = document.createElement('div');
    d.textContent = String(str ?? '');
    return d.innerHTML;
  }

  /** Today's date in the store timezone ("Europe/Berlin") as "YYYY-MM-DD". */
  function todayISO() {
    // en-CA formats as YYYY-MM-DD, matching the sliced current_date shape.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  }

  /**
   * "YYYY-MM-DD…" → "DDMMYYYY" — the key shape `last_orders` is bucketed by.
   * Returns '' when the input is not a parsable ISO date.
   */
  function toOrderDateKey(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
    return m ? `${m[3]}${m[2]}${m[1]}` : '';
  }

  /** "YYYY-MM-DD…" → "DD.MM.YYYY" (tooltip copy). '' when unparsable. */
  function toGermanDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
    return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
  }

  /** Normalise the `categories` field (array or "A, B" string) to trimmed array. */
  function catList(categories) {
    const parts = Array.isArray(categories) ? categories : String(categories ?? '').split(',');
    return parts.map(s => String(s).trim()).filter(Boolean);
  }

  /**
   * Normalise a raw sortIndex (number or string) to a positive integer, or null
   * when it is absent/blank/non-integer/< 1. A KIOS entry counts as "manually
   * sorted" only when this returns a value.
   */
  function toSortIndex(value) {
    if (value === null || value === undefined) return null;
    const s = String(value).trim();
    if (!/^\d+$/.test(s)) return null;
    const n = Number(s);
    return n >= 1 ? n : null;
  }

  /** 1→"001", 2→"012", 3+ digits unchanged. Matches legacy count display. */
  function addNumberPrefix(value) {
    if (value === null || value === undefined) return '';
    const s = String(value).trim();
    if (!/^\d+$/.test(s)) return s;
    return s.length === 1 ? `00${s}` : s.length === 2 ? `0${s}` : s;
  }

  /** Most decimals a sub-cent amount may be shown with before it reads as 0,00. */
  const MAX_MONEY_DECIMALS = 8;

  /**
   * Decimal places to print for `n`: the usual 2, extended just far enough for a
   * non-zero amount smaller than half a cent to stay visible (derived portion /
   * per-KG prices can legitimately land at e.g. 0.0006, which toFixed(2) would
   * flatten to "0,00"). Two significant digits are kept and trailing zeros
   * dropped, so 0.0006 → 4 places ("0,0006") and 0.00456 → 4 ("0,0046").
   */
  function _moneyDecimals(n) {
    if (n === 0 || Math.abs(n) >= 0.005) return 2;         // rounds to a visible cent
    let d = 3;
    while (d < MAX_MONEY_DECIMALS && Number(n.toFixed(d)) === 0) d++;
    d = Math.min(d + 1, MAX_MONEY_DECIMALS);               // keep a 2nd significant digit
    while (d > 2 && n.toFixed(d).endsWith('0')) d--;       // …but no trailing zeros
    return d;
  }

  /**
   * German money formatter with superscript decimals, mirroring the Liquid
   * `price-as-money` output ("18,<sup>79 €</sup>").
   * @param {number|string} amount  Amount in major units (Euros), e.g. 18.79.
   */
  function formatMoney(amount, { superscript = true } = {}) {
    const n = Number(amount);
    if (!isFinite(n)) return '';
    const fixed = n.toFixed(_moneyDecimals(n));
    const [whole, cents] = fixed.split('.');
    return superscript ? `€${whole},<sup>${cents}</sup>` : `€${whole},${cents}`;
  }

  /** Read a product-metafield "nutrition"-style feature by id. */
  function nutrition(product, featureId) {
    const arr = product?.metafields?.nutritions ?? [];
    return arr.find(f => f.feature_id === featureId)?.feature_value ?? null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRICING  (get_assortment_details → price_json, per B2B price groups)
  // ───────────────────────────────────────────────────────────────────────────
  // The API no longer flattens price onto the variant. Instead each variant
  // carries a `price_json` array of price rows, each scoped to a date window
  // (date_from/date_to as DDMMYYYY) and a `user_group_identifier`:
  //   • B2B_DEFAULT_PRICE_GROUP0 → its `price`          → compare_at_price
  //   • B2B_DEFAULT_PRICE_GROUP1 → its `price`          → regular selling price
  //                                                        (only when variant.price is blank)
  //                                its `frontend_price` → price-per-KG
  //                                its `portion_price`  → portion price
  // The active row for a group is the one whose [date_from, date_to] contains the
  // selected delivery date (cart current_date). compare_at_price ALWAYS comes from
  // price_json; the regular price prefers variant.price, falls back to price_json
  // (GROUP1) when it is blank, and finally to the variant's `shopify_default_price`.
  //
  // Portion price / price-per-KG follow the price that won:
  //   • variant.price or shopify_default_price won → they are DERIVED from it and
  //     the variant's weight metafields (see resolvePricing), because a flat price
  //     has no matching price_json row to read frontend_price/portion_price from;
  //   • the GROUP1 row won → its own `portion_price` / `frontend_price` are used,
  //     exactly as before.
  // ═══════════════════════════════════════════════════════════════════════════
  const PRICE_GROUP = {
    COMPARE: 'B2B_DEFAULT_PRICE_GROUP0', // compare_at_price
    REGULAR: 'B2B_DEFAULT_PRICE_GROUP1', // selling price + frontend/portion
  };

  /** A price field counts as present only when it is not null/blank (" " is blank). */
  const hasPrice = v => v != null && String(v).trim() !== '';

  /**
   * A weight metafield counts as usable only when it is present AND a number > 0
   * ("0" / "" / "N/A" → null), so it can never divide or multiply a price into
   * 0/NaN/Infinity.
   */
  const positiveNumber = v => {
    if (!hasPrice(v)) return null;
    const n = Number(v);
    return isFinite(n) && n > 0 ? n : null;
  };

  /** "DDMMYYYY" → sortable YYYYMMDD number (null when not 8 digits). */
  function _jsonDateToNum(s) {
    const str = String(s ?? '').trim();
    if (!/^\d{8}$/.test(str)) return null;
    return Number(str.slice(4, 8) + str.slice(2, 4) + str.slice(0, 2));
  }

  /** "YYYY-MM-DD…" (cart current_date, already sliced) → sortable YYYYMMDD number. */
  function _selectedDateToNum(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
    return m ? Number(m[1] + m[2] + m[3]) : null;
  }

  /**
   * First price_json row for `group` whose date window contains `selNum`
   * (a YYYYMMDD number). Returns null when nothing matches.
   */
  function _pickPriceRow(priceJson, group, selNum) {
    if (!Array.isArray(priceJson) || selNum == null) return null;
    return priceJson.find(row => {
      if (row?.user_group_identifier !== group) return false;
      const from = _jsonDateToNum(row.date_from);
      const to = _jsonDateToNum(row.date_to);
      return from != null && to != null && from <= selNum && selNum <= to;
    }) ?? null;
  }

  /**
   * Resolve every displayable price for a variant against the selected date.
   *
   * Portion price / price-per-KG are DERIVED from the selling price whenever that
   * price came from the variant itself (`variant.price`, or `shopify_default_price`
   * when there is no GROUP1 row either), using the variant's weight metafields:
   *
   *   perUnit      = price / netto_weight_sales_unit        (price per gram)
   *   portionPrice = perUnit * portion_raw_weight           (needs portion_raw_weight
   *                                                          + unit_at_portion_price)
   *   pricePerKg   = perUnit * 1000
   *
   * When the GROUP1 row supplied the selling price — or the metafields needed for
   * the formula are missing — the row's own `portion_price` / `frontend_price` are
   * used, exactly as before.
   *
   * @returns {{ price:number|null, compareAtPrice:number|null,
   *             portionPrice:number|null, pricePerKg:number|null }} (all in €)
   */
  function resolvePricing(variant, selectedDateIso) {
    const priceJson = Array.isArray(variant?.price_json) ? variant.price_json : [];
    const selNum = _selectedDateToNum(selectedDateIso);
    const g0 = _pickPriceRow(priceJson, PRICE_GROUP.COMPARE, selNum);
    const g1 = _pickPriceRow(priceJson, PRICE_GROUP.REGULAR, selNum);

    // Regular selling price, first present candidate wins:
    //   1. variant.price  2. GROUP1 row's price  3. variant.shopify_default_price
    //   4. null when none of them is present (or the value is not a number).
    const candidate = [variant?.price, g1?.price, variant?.shopify_default_price].find(hasPrice);
    const priceNum = candidate === undefined ? NaN : Number(candidate);
    const price = isFinite(priceNum) ? priceNum : null;

    // Price-per-gram, only when the winning price came from the variant (a GROUP1
    // price keeps its own frontend/portion values) and the sales weight is usable.
    const fromGroup = !hasPrice(variant?.price) && hasPrice(g1?.price);
    const mf = variant?.metafields ?? {};
    const nettoWeight = positiveNumber(mf.netto_weight_sales_unit);
    const perUnit = !fromGroup && price != null && nettoWeight != null ? price / nettoWeight : null;

    // Portion additionally needs both portion metafields (weight AND its unit);
    // per-KG only needs the sales weight.
    const portionWeight = positiveNumber(mf.portion_raw_weight);
    const derivedPortion = perUnit != null && portionWeight != null && hasPrice(mf.unit_at_portion_price)
      ? perUnit * portionWeight : null;
    const derivedPerKg = perUnit != null ? perUnit * 1000 : null;

    return {
      price,
      compareAtPrice: g0 && hasPrice(g0.price) ? Number(g0.price) : null,
      portionPrice: derivedPortion ?? (g1 && hasPrice(g1.portion_price) ? Number(g1.portion_price) : null),
      pricePerKg: derivedPerKg ?? (g1 && hasPrice(g1.frontend_price) ? Number(g1.frontend_price) : null),
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VARIANT SELECTION  (mirrors sections/product-card-assortment.liquid logic)
  // ═══════════════════════════════════════════════════════════════════════════
  const isOrderable = v => v?.metafields?.orderable === 'J';

  /**
   * Pick the variant the card displays + whether it is orderable.
   * Per spec: the card is driven by the `selected: true` variant (fallback: first).
   * Availability is that variant's own `metafields.orderable === 'J'`.
   * @returns {{ variant: object|null, orderable: boolean }}
   */
  function resolveVariant(product) {
    const variants = product?.variants ?? [];
    if (!variants.length) return { variant: null, orderable: false };
    const variant = variants.find(v => v.selected) ?? variants[0];
    return { variant, orderable: isOrderable(variant) };
  }

  /**
   * Normalise a variant's `follow_up_product` metafield into a single object,
   * or null when there is no replacement. Accepts either an array (first entry)
   * or a plain object. See GAP LIST for the required shape.
   */
  function followUpOf(variant) {
    const raw = variant?.metafields?.follow_up_product;
    if (!raw) return null;
    const obj = Array.isArray(raw) ? raw[0] : raw;
    if (!obj || typeof obj !== 'object') return null;
    return obj;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STATIC SVG SNIPPETS
  // ═══════════════════════════════════════════════════════════════════════════
  const SVG = {
    heart: `<svg class="icon icon--heart" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation"><path fill="currentColor" d="M8.86 4.38a2.78 2.78 0 013.72-.3c1.4 1.2 1.2 3.11.19 4.13L7.98 13.1c-.05.06-.1.06-.19 0L3.01 8.2a2.8 2.8 0 01.19-4.1c1.06-.9 2.7-.76 3.74.28l.96.98.96-.98zm-.96-.45l.24-.25a3.78 3.78 0 015.07-.38l.01.01v.01a3.82 3.82 0 01.26 5.59l-4.79 4.9a1.12 1.12 0 01-1.45.12l-.1-.06L2.3 8.91a3.8 3.8 0 01.26-5.57 3.79 3.79 0 015.1.33l.01.01.24.25z" fill-rule="evenodd"></path></svg>`,
    tagCross: `<svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linejoin="round" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M5 19 19 5M5 5l14 14"></path></svg>`,
    tagCheck: `<svg style="width:24px;height:24px" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" class="icon"><path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8m4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8-1.41-1.42Z"></path></svg>`,
    delete: `<svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"></path><path d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5zm2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5z" fill="currentColor"></path></svg>`,
    placeholder: `<svg class="media__placeholder img-fit" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path><path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2z"></path></svg>`,
    sortIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="22px" height="22px" viewBox="0 0 24 24" fill="none"> <g> <path d="M16 18L16 16M16 6L20 10.125M16 6L12 10.125M16 6L16 13" stroke="#3C0037" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M8 18L12 13.875M8 18L4 13.875M8 18L8 11M8 6V8" stroke="#3C0037" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </g> </svg>`,
    moveArrow: `<svg class="icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 1920 1920" fill="currentColor" stroke="currentColor" stroke-width="40" aria-hidden="true" focusable="false" role="presentation"><path d="M1805.937 276v1368.756H1920V276h-114.063Zm-739.106 73.765-80.642 80.642 473.02 473.02H0v113.948h1459.208l-473.02 473.02 80.643 80.642 610.694-610.693-610.694-610.58Z" fill-rule="evenodd"></path></svg>`,
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LAST-ORDERED INDICATOR  (theme setting `enable_last_order_indicator`)
  // ───────────────────────────────────────────────────────────────────────────
  // A truck badge shown on every card whose variant already appears in the
  // `last_orders` bucket of the SELECTED delivery date. `last_orders` comes back
  // from get_assortment_details keyed by "DDMMYYYY":
  //   { "11082026": [{ variant_id, quantity }, …], … }
  // It is folded into a variantId → quantity Map once (see _buildLastOrderIndex)
  // so a card only ever costs one O(1) lookup while its HTML is built.
  // ═══════════════════════════════════════════════════════════════════════════
  /**
   * Markup for one indicator, injected directly above the card's <product-form>.
   *
   * A card carries ONE of these per ORDERED VARIANT, not just for the variant it
   * opens on: the quantity is variant-specific, so switching the variant picker
   * has to swap the badge without re-rendering the card. Only the selected
   * variant's badge is visible; the others ship with `hidden-i` and are toggled by
   * VariantPicker.updateOrderTruck() (assets/variant-picker.js), which matches on
   * `data-varinat-id`.
   *
   * @param {string|number} variantId  Shopify variant id of the ordered variant.
   * @param {number}        quantity   Quantity already ordered for that date.
   * @param {string}        isoDate    Delivery date, "YYYY-MM-DDT00:00:00.000Z".
   * @param {string}        displayDate Same date as "DD.MM.YYYY" (tooltip copy).
   * @param {boolean}       [hidden]   Render hidden — not the selected variant.
   */
  function lastOrderIndicatorHtml(variantId, quantity, isoDate, displayDate, hidden = false) {
    const qty = escHTML(quantity);
    const day = escHTML(displayDate);
    return `<div class="flex order-truck-container mb-2${hidden ? ' hidden-i' : ''}" data-order-text="${escAttr(isoDate)}" data-order-quantity="${escAttr(quantity)}" data-varinat-id="${escAttr(variantId)}">
        <div class="order-truck">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 70" width="60" height="32" aria-hidden="true" focusable="false">
            <g fill="none" stroke="#3C0037" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 12 H58 V38 H98 Q108 38 108 48 V52 H102 A10 10 0 0 0 82 52 H44 A10 10 0 0 0 24 52 H8 Z"></path>
              <path d="M58 20 H78 L95 35 V38 H58 Z"></path>
              <circle cx="34" cy="54" r="8"></circle>
              <circle cx="92" cy="54" r="8"></circle>
              <circle cx="34" cy="54" r="2.5"></circle>
              <circle cx="92" cy="54" r="2.5"></circle>
            </g>
            <text x="35" y="37" text-anchor="middle" font-size="23" fill="#3C0037">${qty}</text>
          </svg>
          <div class="truck-tooltip">Dieser Artikel wurde bereits für das gewählte Lieferdatum <strong>${day}</strong> bestellt. Die Zahl <strong>${qty}</strong> gibt die bereits bestellte Menge an.</div>
        </div>
      </div>`;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CARD BUILDER  — renders one product card <li> from JSON.
  // Reproduces the DOM/classes of snippet product-card-assortment so the
  // existing CSS (my-assortment.css / product.css) styles it identically.
  // ═══════════════════════════════════════════════════════════════════════════
  class CardBuilder {
    /**
     * @param {object} ctx  { customer, customTags, kiosById, imageWidths }
     */
    constructor(ctx) {
      this.ctx = ctx;
    }

    /** Build the media (image or placeholder). Uses `image_url` when present. */
    _media(product, url) {
      const imageUrl = product.image_url;
      if (!imageUrl) {
        return `<a href="${escAttr(url)}" aria-label="${escAttr(product.title)}" class="media block relative image-blend js-prod-link" style="padding-top:100%;" tabindex="-1">${SVG.placeholder}</a>`;
      }
      const widths = [320, 460, 600, 700, 800];
      const sep = imageUrl.includes('?') ? '&' : '?';
      const srcset = widths.map(w => `${imageUrl}${sep}width=${w} ${w}w`).join(', ');
      return `<a href="${escAttr(url)}" aria-label="${escAttr(product.title)}" class="media block relative image-blend js-prod-link" style="padding-top:100%;" tabindex="-1">
          <img data-srcset="${escAttr(srcset)}" sizes="(min-width:1280px) calc((100vw - 200px) / 4), (min-width:769px) calc((100vw - 168px) / 4), (min-width:600px) calc((100vw - 144px) / 3), calc(100vw - 40px)" src="${escAttr(imageUrl + sep + 'width=460')}" class="img-fit img-fit--contain card__main-image no-js-hidden" loading="lazy" alt="${escAttr(product.title)}">
        </a>`;
    }

    /** Build the price block from the display variant (values via price_json). */
    _price(variant) {
      const pricing = resolvePricing(variant, this.ctx.currentDate?.());
      const price = Number(pricing.price);
      const compare = Number(pricing.compareAtPrice);
      const isOffer = isFinite(compare) && isFinite(price) && compare > price;
      const portion = this._portion(variant, pricing);
      const perKg = this._perKg(variant, pricing);
      return `<div class="product-card__price product-info__price flex grow items-end justify-center gap-2">
        <div class="price${isOffer ? ' price--on-sale' : ''} price--sale-color price--bottom">
          <div class="price__default">
            <span class="price__current">${formatMoney(price)}</span>
            <s class="price__was price__was--supercents">${isOffer ? formatMoney(compare, { superscript: false }) : ''}</s>
          </div>
          ${portion}
          ${perKg}
          <div class="price__no-variant" hidden><span class="price__current">Nicht verfügbar</span></div>
        </div>
      </div>`;
    }

    /**
     * Portion price block. The value comes from resolvePricing — derived from the
     * variant's own price + weight metafields, or the GROUP1 `portion_price` row;
     * the label weight/unit always come from the variant metafields. Renders only
     * when a portion price exists and weight is non-zero.
     */
    _portion(variant, pricing) {
      const mf = variant.metafields ?? {};
      const portionPrice = pricing?.portionPrice ?? null;
      const rawWeight = mf.portion_raw_weight;
      const unit = (mf.unit_at_portion_price ?? '').toLowerCase();
      if (portionPrice == null || !rawWeight || rawWeight === '0') return '';
      return `<div class="mt-2 portion__price relative" data-portion-price>
          <span class="visually-hidden">Portionspreis</span>
          <span class="unit-price__price">Portionspreis ${escHTML(rawWeight)}&nbsp;${escHTML(unit)}</span>
          <span class="unit-price__separator"> = </span>
          <span class="unit-price__unit">${formatMoney(portionPrice, { superscript: false })}</span>
        </div>`;
    }

    /**
     * Per-KG block. The value comes from resolvePricing — derived from the variant's
     * own price + netto_weight_sales_unit, or the GROUP1 `frontend_price` row; still
     * gated on the variant being bulk (is_product_bulk === 'J').
     */
    _perKg(variant, pricing) {
      const mf = variant.metafields ?? {};
      const unitPrice = pricing?.pricePerKg ?? null;
      if (mf.is_product_bulk !== 'J' || unitPrice == null) return '';
      return `<div class="price__per-kg mt-2">je KG ${formatMoney(unitPrice, { superscript: false })}</div>`;
    }

    /**
     * Discount ribbon shown when compare_at_price > price. Always rendered (hidden
     * when there is no discount) so variant-picker.js can toggle/update it on
     * variant change via updateDiscountRibbons(). Mirrors snippet discount-ribbons:
     * only shows when the rounded saving is > 1 %.
     */
    _compareDiscount(pricing) {
      const price = Number(pricing.price);
      const compare = Number(pricing.compareAtPrice);
      const hidden = `<compare-discount hidden data-string="Sparen Sie [amount]!"></compare-discount>`;
      if (!isFinite(price) || !isFinite(compare) || compare <= price) return hidden;
      const pct = Math.round(((compare - price) * 100) / compare);
      if (pct <= 1) return hidden;
      return `<compare-discount class="" data-string="Sparen Sie [amount]!" data-compare-price="${escAttr(compare)}" data-price="${escAttr(Math.round(price))}">Sparen Sie ${pct} %</compare-discount>`;
    }

    /**
     * Stock-level note — mirrors snippet new-inventory-info.liquid.
     * Only when the variant is orderable and the `inventories` metafield has a
     * row whose `date` matches the selected delivery date (cart current_date).
     *   qty > 999            → in stock (no count)
     *   0 < qty <= 999       → in stock (count)
     *   qty <= 0             → no stock
     * No matching date → nothing (same as the old card when no delivery date).
     */
    _stock(variant) {
      if (variant.metafields?.orderable !== 'J') return '';
      const inv = variant.metafields?.inventories;
      const date = this.ctx.currentDate?.();
      if (!Array.isArray(inv) || !inv.length || !date) return '';
      const match = inv.find(i => String(i.date ?? '').slice(0, 10) === date);
      if (!match) return '';
      const qty = Number(match.quantity ?? 0) || 0;

      const t = this.ctx.inventoryTexts ?? {};
      const infoIcon = `<svg height="16px" width="16px" viewBox="0 0 512 512" fill="#3c0037" stroke="#3c0037"> <g> <path class="st0" d="M255.992,0.008C114.626,0.008,0,114.626,0,256s114.626,255.992,255.992,255.992 C397.391,511.992,512,397.375,512,256S397.391,0.008,255.992,0.008z M300.942,373.528c-10.355,11.492-16.29,18.322-27.467,29.007 c-16.918,16.177-36.128,20.484-51.063,4.516c-21.467-22.959,1.048-92.804,1.597-95.449c4.032-18.564,12.08-55.667,12.08-55.667 s-17.387,10.644-27.709,14.419c-7.613,2.782-16.225-0.871-18.354-8.234c-1.984-6.822-0.404-11.161,3.774-15.822 c10.354-11.484,16.289-18.314,27.467-28.999c16.934-16.185,36.128-20.483,51.063-4.524c21.467,22.959,5.628,60.732,0.064,87.497 c-0.548,2.653-13.742,63.627-13.742,63.627s17.387-10.645,27.709-14.427c7.628-2.774,16.241,0.887,18.37,8.242 C306.716,364.537,305.12,368.875,300.942,373.528z M273.169,176.123c-23.886,2.096-44.934-15.564-47.031-39.467 c-2.08-23.878,15.58-44.934,39.467-47.014c23.87-2.097,44.934,15.58,47.015,39.458 C314.716,152.979,297.039,174.043,273.169,176.123z"></path></g></svg>`;
      const count = (t.available_count ?? '').replace('__COUNT__', String(qty));
      const tip = t.tooltip ? `<span class="tooltiptext">${escHTML(t.tooltip)}</span>` : '';

      let body;
      if (qty <= 0) {
        body = `<div>${escHTML(t.no_stock ?? '')}</div>`;
      } else if (qty <= 999) {
        body = `<div>${escHTML(t.in_stock ?? '')} (${escHTML(count)})</div>${tip}`;
      } else {
        body = `<div>${escHTML(t.in_stock ?? '')}</div>`;
      }
      const level = qty <= 0 ? 'sold' : qty <= 999 ? 'high' : 'very_high';
      return `<div class="pt-2 text-sm flex gap-2 items-center" data-stock-inventory data-variant-id="${escAttr(variant.id)}" data-inventory-qty="${escAttr(qty)}" data-inventory-level="${level}">${infoIcon}${body}</div>`;
    }

    /** Assortment-interact (heart + custom-tag connect list + kio state). */
    _interact(product, variant, kiosEntry) {
      const { customer, customTags } = this.ctx;
      const active = Boolean(kiosEntry);
      const currentKio = kiosEntry
        ? JSON.stringify({
            id: kiosEntry.id ?? null,
            variantId: `gid://shopify/ProductVariant/${stripVariantGid(kiosEntry.variantId ?? variant.id)}`,
            productId: `gid://shopify/Product/${stripProductGid(kiosEntry.productId ?? product.id)}`,
            categories: kiosEntry.categories ?? '',
            handle: product.handle,
            sales: kiosEntry.sales ?? null,
          })
        : '';

      const listItems = customTags.map(t => {
        const included = t.handles.includes(product.handle);
        const count = t.handles.filter(h => h?.trim()).length;
        return `<li class="flex flex-row" data-filter="${escAttr(t.name)}" data-included="${included}" data-count="${count}">
            <span data-not-included>${SVG.tagCross}</span>
            <span data-is-included>${SVG.tagCheck}</span>
            <span>${escHTML(t.name.split(':')[0])}</span>
            <span data-count>${count}</span>
          </li>`;
      }).join('');

      return `<assortment-interact data-user="${escAttr(customer.userNumber ?? '')}" data-user-name="${escAttr(customer.userName ?? '')}" data-customer-id="${escAttr(customer.id ?? '')}" data-active="${active}" class="product-header__toolbar relative" data-variant-id="${escAttr(variant.id)}" data-handle="${escAttr(product.handle)}" data-product-id="${escAttr(product.id)}">
        <button tabindex="-1" class="btn btn--primary">${SVG.heart}</button>
        <script type="application/json" data-current-kio>${currentKio}</script>
        <div class="absolute assortment-custom-list"><ul>${listItems}</ul></div>
      </assortment-interact>`;
    }

    /** Filter badges (custom tags this product currently belongs to). */
    _badges(product) {
      const badges = this.ctx.customTags
        .filter(t => t.handles.includes(product.handle))
        .map(t => {
          const [name, color] = t.name.split(':');
          return `<div class="badge-item" data-badge-filter="${escAttr(t.name)}"><span style="background-color:${escAttr(color)};">${escHTML(name)}</span></div>`;
        }).join('');
      return `<div class="flex flex-wrap gap-2 justify-center items-center mt-3" data-filter-badges>${badges}</div>`;
    }

    /**
     * "Sortieren" action, rendered directly below the price block. An inline
     * integer input plus an OK button — no modal. The wrapper carries the
     * product/variant identity the submit needs (same fields CREATE_KIO sends)
     * plus the product's CURRENT position; the input accepts [1, number of
     * orderable products] — not-orderable products are shown apart in
     * `[data-sale-unavailable]` and are left out of the numbering, so the main
     * grid reads 1, 2, 3, … with no gaps.
     *
     * EVERY card here is prefilled with its own current position in the grid
     * (`ctx.sortPosition()` → _buildSortPositions), so the field always reads as
     * "this product sits here". Typing a different number and confirming moves the
     * product to that position and shifts everything in between by one, exactly
     * like dragging a row in a list (see MyAssortment._computeSortIndexes).
     * `data-sort-index` mirrors the prefilled position so a submit can detect a
     * no-op move without re-deriving it.
     *
     * The input is wrapped in its own `<label>` (implicit association — cards can
     * be mounted in both the main and the filtered view, so no ids are minted) so
     * the visible "Position" caption is also the accessible name. The submit button
     * is icon-only: `aria-label` carries the name and the `.tooltip` wrapper shows
     * "Position ändern" on hover. The tooltip lives on the wrapper, not the button,
     * because `.is-loading` already owns the button's ::before/::after.
     */
    _sortAction(product, variant, kiosEntry) {
      const position = toSortIndex(this.ctx.sortPosition?.(kiosEntry));
      const max = Math.max(this.ctx.sortMax(), position ?? 0);
      return `<div class="assortment-sort flex flex-wrap items-end gap-2 mt-3" data-sort-action data-product-id="${escAttr(product.id)}" data-variant-id="${escAttr(variant.id)}" data-handle="${escAttr(product.handle)}" data-sort-index="${position != null ? escAttr(position) : ''}">
        <label class="assortment-sort__group">
          <span class="assortment-sort__label">Position</span>
          <span class="assortment-sort__field inline-flex items-center relative">
            ${SVG.sortIcon}
            <input type="number" inputmode="numeric" step="1" min="1" max="${escAttr(max)}" class="assortment-sort__input input" placeholder="Sortierindex" value="${position != null ? escAttr(position) : ''}" data-sort-input>
          </span>
        </label>
        <span class="tooltip assortment-sort__tip" data-tooltip="Position ändern">
          <button type="button" class="btn btn--primary assortment-sort__ok" aria-label="Position ändern" data-sort-ok>${SVG.moveArrow}</button>
        </span>
      </div>`;
    }

    /** Variant picker (radio when one variant, dropdown otherwise) + the data-product-json blob variant-picker.js reads. */
    _variantPicker(product, variant) {
      const variants = product.variants ?? [];
      const productJson = this._buildVariantPickerJSON(product, variant);
      const idBase = `mya-${escAttr(product.id)}-size`;

      let selector;
      if (variants.length <= 1) {
        const value = escAttr(variant.option1 ?? variant.title);
        selector = `<div class="option-selector" data-selector-type="listed">
          <legend class="label hidden-i">Size: </legend>
          <div class="option-selector__btns flex flex-wrap">
            <input type="radio" tabindex="-1" class="block-opt-css opt-btn visually-hidden focus-label js-option" name="${idBase}-option" id="${idBase}-opt-0" value="${value}" data-selected="${value}" checked>
            <label class="block-opt-css opt-label opt-label--btn btn relative text-center" for="${idBase}-opt-0">
              <span>${escHTML(variant.option1 ?? variant.title)}</span>
            </label>
          </div>
        </div>`;
      } else {
        const options = variants.map((v, i) => {
          const selected = String(v.id) === String(variant.id);
          return `<li data-viewable="J" data-orderable="${v.metafields?.orderable ?? ''}" class="custom-select__option flex items-center js-option" id="${idBase}-opt-${i}" role="option" data-value="${escAttr(v.option1 ?? v.title)}"${selected ? ' aria-selected="true"' : ''} tabindex="-1">
              <span class="pointer-events-none">${escHTML(v.option1 ?? v.title)}</span>
              <em class="pointer-events-none" hidden>Nicht verfügbar</em>
            </li>`;
        }).join('');
        selector = `<div class="option-selector" data-selector-type="dropdown">
          <custom-select id="${idBase}"><div class="custom-select relative w-full no-js-hidden">
            <button class="custom-select__btn input items-center" type="button" aria-expanded="false" aria-haspopup="listbox" id="${idBase}-button" tabindex="-1">
              <span class="text-start">${escHTML(variant.option1 ?? variant.title)}</span>
              <svg width="20" height="20" viewBox="0 0 24 24" class="icon" role="presentation" focusable="false" aria-hidden="true"><path d="M20 8.5 12.5 16 5 8.5" stroke="currentColor" stroke-width="1.5" fill="none"></path></svg>
            </button>
            <ul class="custom-select__listbox absolute invisible" role="listbox" aria-hidden="true" hidden>${options}</ul>
          </div></custom-select>
        </div>`;
      }

      return `<variant-picker class="no-js-hidden" data-current-variant="${escAttr(variant.id)}" data-url="/products/${escAttr(product.handle)}" data-current-handle="search" data-update-url="false" data-show-availability="" data-availability-method="selection" data-in-kio="true" data-kio-variant-id="${escAttr(variant.id)}">
        ${selector}
        <script type="application/json" data-product-json>${productJson}</script>
      </variant-picker>`;
    }

    /**
     * Minimal `data-product-json` for variant-picker.js. Every price (regular,
     * compare, portion, per-kg) is resolved from each variant's `price_json`
     * against the selected delivery date via resolvePricing(), so switching a
     * variant shows the correct date-scoped B2B prices and discount ribbon.
     */
    _buildVariantPickerJSON(product, current) {
      const selectedDate = this.ctx.currentDate?.();
      const cents = v => (v == null ? 0 : Math.round((Number(v) + Number.EPSILON) * 100));
      // productId(number) → resolved pricing, so we resolve each variant once.
      const pricingById = new Map();
      const pricingOf = v => {
        const key = Number(v.id);
        if (!pricingById.has(key)) pricingById.set(key, resolvePricing(v, selectedDate));
        return pricingById.get(key);
      };

      const variants = (product.variants ?? []).map(v => {
        const p = pricingOf(v);
        return {
          id: Number(v.id),
          title: v.title,
          option1: v.option1, option2: v.option2, option3: v.option3,
          sku: v.identifier,
          requires_shipping: true,
          taxable: true,
          featured_image: null,
          available: isOrderable(v),
          price: cents(p.price),
          compare_at_price: cents(p.compareAtPrice),
          barcode: v.barcode,
          options: [v.option1, v.option2, v.option3].filter(Boolean),
        };
      });
      const formatted = {};
      for (const v of product.variants ?? []) {
        const f = v.formatted ?? {};
        const p = pricingOf(v);
        const isOffer = p.compareAtPrice != null && p.price != null && Number(p.compareAtPrice) > Number(p.price);
        formatted[Number(v.id)] = {
          price: formatMoney(p.price),
          compareAtPrice: isOffer ? formatMoney(p.compareAtPrice, { superscript: false }) : '',
          portionPrice: this._portion(v, p),
          customer_portion_price: p.portionPrice != null ? Number(p.portionPrice) * 100 : '',
          weight: f.weight ?? v.weight,
          barcode: f.barcode ?? v.barcode,
          identifier: v.identifier,
          variant_price: cents(p.price),
          variant_compare_at_price: cents(p.compareAtPrice),
          price_per_kg: v.metafields?.is_product_bulk === 'J' && p.pricePerKg != null ? cents(p.pricePerKg) : '',
          variant_id: Number(v.id),
          variant_title: v.title,
          variant_options: [v.option1, v.option2, v.option3].filter(Boolean),
          sku: v.identifier,
          deposit_reference: v.metafields?.deposit_reference ?? '',
          netto_weight_sales_unit: Number(v.metafields?.netto_weight_sales_unit ?? 0),
          isAvailable: v.metafields?.orderable,
          bonus_product: v.metafields?.bonus_product ?? '',
          europe1_price_factory_ptg: v.metafields?.europe1_price_factory_ptg,
          unit_at_portion_price: v.metafields?.unit_at_portion_price,
          portion_raw_weight: v.metafields?.portion_raw_weight,
          is_product_bulk: v.metafields?.is_product_bulk,
        };
      }
      return JSON.stringify({
        product: { id: Number(product.id), title: product.title, handle: product.handle, tags: product.tags, vendor: product.vendor, type: product.type, featured_image: product.image_url, variants, options: product.options.map(opt => opt.name) ?? [] },
        formatted,
        product_metafields: { identifier: product.identifier, nutritions: product.metafields?.nutritions ?? [] },
      });
    }

    /** The buy area. Branches: orderable → cart; else follow-up → swap; else sold-out. */
    _buyArea(product, variant, orderable, followUp) {
      const availability = JSON.stringify((product.variants ?? []).map(v => ({
        variantId: Number(v.id), isAvailable: v.metafields?.orderable ?? 'N',
      })));
      const inner = orderable
        ? this._cartControls(product, variant)
        : followUp
          ? this._followUpControls(product, variant, followUp)
          : `<sold-out-warning data-variant-sold-out data-availibility="${escAttr(availability)}" data-id="${escAttr(product.id)}" class="sold-out-warning">Ausverkauft</sold-out-warning>`;

      // Last-ordered badges — one per ordered variant of this product (all but the
      // selected one hidden). '' when the feature is off or nothing was ordered.
      return `<div class="product-info__block product-info__block-buy-buttons">
        ${this.ctx.lastOrderIndicator?.(product, variant) ?? ''}
        <product-form data-handle="${escAttr(followUp?.handle ?? product.handle)}">
          <form method="post" action="/cart/add" id="quick-add-product-card-assortment${escAttr(product.id)}" accept-charset="UTF-8" class="js-product-form js-product-form-main" enctype="multipart/form-data" novalidate="novalidate">
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">
            <input type="hidden" name="id" value="${escAttr(variant.id)}">
            <input type="hidden" name="handle" value="${escAttr(product.handle)}">
            <div class="alert mb-8 bg-error-bg text-error-text js-form-error text-start" role="alert" hidden></div>
            <div class="product-info__add-to-cart flex flex-col gap-2">${inner}</div>
          </form>
        </product-form>
      </div>`;
    }

    /** Orderable path: variant picker + quantity + hidden sold-out fallback. */
    _cartControls(product, variant) {
      const depositRef = variant.metafields?.deposit_reference ?? '';
      // Regular selling price (variant.price, else GROUP1 price_json), in cents.
      const sellingPrice = resolvePricing(variant, this.ctx.currentDate?.()).price;
      const price = sellingPrice == null ? 0 : Math.round((Number(sellingPrice) + Number.EPSILON) * 100);
      const availability = JSON.stringify((product.variants ?? []).map(v => ({
        variantId: Number(v.id), isAvailable: v.metafields?.orderable ?? 'N',
      })));
      return `${this._variantPicker(product, variant)}
        <quantity-input class="inline-block flex w-full" data-variant-identifier="${escAttr(variant.identifier)}" data-variant-price="${escAttr(price)}" data-deposit-reference="${escAttr(depositRef)}" data-line-item="" data-variant-id="${escAttr(variant.id)}" data-product-handle="${escAttr(product.handle)}" data-product-id="${escAttr(product.id)}" data-update-cart="true" data-allow-more-than-max-qty="true">
          <div class="qty-input qty-input--combined inline-flex items-center w-full">
            <button tabindex="-1" type="button" class="qty-input__btn btn btn--minus no-js-hidden" name="minus"><span class="visually-hidden">-</span></button>
            <input type="number" class="qty-input__input input" name="quantity" min="0" value="" data-variant-id="${escAttr(variant.id)}">
            <button tabindex="-1" type="button" class="qty-input__btn btn btn--plus no-js-hidden" name="plus"><span class="visually-hidden">+</span></button>
          </div>
        </quantity-input>
        <sold-out-warning data-variant-sold-out data-availibility="${escAttr(availability)}" data-id="${escAttr(product.id)}" hidden class="sold-out-warning">Ausverkauft</sold-out-warning>`;
    }

    /**
     * Not-orderable path with a replacement (follow-up) product:
     * "Folgeartikel anzeigen" link + "Artikel tauschen" swap button.
     * The swap button carries everything my-assortment._changeFollowUp needs.
     */
    _followUpControls(product, variant, followUp) {
      const newHandle = followUp.handle ?? '';
      const newProductId = stripProductGid(followUp.productShopifyId ?? followUp.productId ?? followUp.id ?? '');
      const newVariantId = stripVariantGid(followUp.variantShopifyId ?? followUp.variantId ?? '');
      const newCategory = followUp.category ?? followUp.categories ?? '';
      // Custom tags that currently contain this product → move to the follow-up on swap.
      const tags = this.ctx.customTags.filter(t => t.handles.includes(product.handle)).map(t => t.name);
      return `<div class="flex flex-col items-center gap-2">
          <p class="text-center mb-0 sold-out-text">Dein Artikel ist leider ausverkauft.</p>
          <a href="/products/${escAttr(newHandle)}" class="card--product__go-to-followup btn btn--primary w-full">Folgeartikel anzeigen</a>
          <button class="card--product__change-assortment-product btn btn--primary w-full" type="button"
            data-current-product-id="${escAttr(product.id)}"
            data-current-variant-id="${escAttr(variant.id)}"
            data-current-handle="${escAttr(product.handle)}"
            data-new-variant-id="${escAttr(newVariantId)}"
            data-new-product-id="${escAttr(newProductId)}"
            data-new-handle="${escAttr(newHandle)}"
            data-new-variant-category="${escAttr(newCategory)}">
            <script type="application/json" data-product-tags>${JSON.stringify({ tags })}</script>
            Artikel tauschen
          </button>
        </div>`;
    }

    /**
     * Build the full <li> card for a product.
     *
     * Not-orderable products render into the separate `[data-sale-unavailable]`
     * block (see MyAssortment._appendCards), so they carry no sort action: they
     * are skipped by the position numbering entirely (_buildSortPositions), which
     * keeps the main grid reading 1, 2, 3, … with no gaps. Two things mark them
     * out: the `assortment-item--unavailable` class on the `<li>` (styling hook)
     * and a notice above the barcode, worded by whether a follow-up exists.
     * @returns {string} HTML
     */
    build(product, kiosEntry, group = {}) {
      const { variant, orderable } = resolveVariant(product);
      if (!variant) return '';

      const followUp = orderable ? null : followUpOf(variant);
      const url = `/products/${product.handle}`;
      const barcode = variant.identifier ?? product.identifier ?? '';
      // Parent category label (old grouping value) + sub-category title (new).
      const parentLabel = group.parentLabel ?? this.ctx.categoryLabel(product);
      const subName = group.subName ?? (kiosEntry?.subCategoryName || parentLabel);

      // Not-orderable notice, directly above the barcode. With a follow-up the
      // customer is asked to swap (the swap button lives in the buy area).
      const unavailableLabel = orderable
        ? ''
        : `<p class="card__unavailable-label mt-2 mb-0${followUp ? ' card__unavailable-label--swap' : ''}">${followUp ? 'Aktuell nicht bestellbar - BITTE AUSTAUSCHEN' : 'Aktuell nicht bestellbar'}</p>`;

      const titleBlock = followUp
        ? `<p class="card__title font-bold mt-1 mb-0 text-theme-light line-through assortment-followup-title"><a tabindex="-1" href="${escAttr(url)}" class="card-link text-current js-prod-link" data-title>${escHTML(product.title)}</a></p>
           <p class="card__title font-bold mt-1 mb-0 main-product-title-follow"><a tabindex="-1" href="${escAttr(url)}" class="card-link text-current js-prod-link" data-title>${escHTML(product.title)}</a></p>
           <p class="card__title font-bold mt-0 mb-0 follow-up-title">Folgeartikel: ${escHTML(followUp.title ?? '')}</p>`
        : `<p class="card__title font-bold mt-1 mb-0"><a tabindex="-1" href="${escAttr(url)}?variant=${variant.id}" class="card-link text-current js-prod-link" data-title>${escHTML(product.title)}</a></p>`;

      const card = `<product-card class="card card--product h-full card--product-contained card--no-lines relative flex js-product${followUp ? ' has-follow-up' : ''}" data-product-handle="${escAttr(followUp?.handle ?? product.handle)}" data-section="product-card-assortment">
        ${this._interact(product, variant, kiosEntry)}
        <div class="card__media relative">${this._media(product, url)}</div>
        <div class="card__info-container flex flex-col flex-auto relative">
          <div class="card__info w-full">
            <div class="card__info-inner flex flex-col h-full text-center w-full">
              <p class="card__sku mt-1 text-theme-light js-product-barcode">${escHTML(barcode)}</p>
              ${titleBlock}
              ${this._stock(variant)}
              ${this._price(variant)}
              ${unavailableLabel}
              ${orderable && this.ctx.enableCustomSort?.() ? this._sortAction(product, variant, kiosEntry) : ''}
              ${this._badges(product)}
            </div>
          </div>
        </div>
        ${this._buyArea(product, variant, orderable, followUp)}
        ${this._compareDiscount(resolvePricing(variant, this.ctx.currentDate?.()))}
      </product-card>`;

      const dataJson = JSON.stringify({
        product: {
          id: Number(product.id), handle: product.handle, title: product.title,
          available: orderable, vendor: product.vendor ?? null, tags: product.tags ?? [],
        },
      });

      return `<li class="js-pagination-result${orderable ? '' : ' assortment-item--unavailable'}" data-product-id="${escAttr(product.id)}" data-parent-category-title="${escAttr(parentLabel)}" data-category-title="${escAttr(subName)}">
        <div id="shopify-section-product-card-assortment" class="shopify-section" data-bulk-item data-handle="${escAttr(product.handle)}">
          <script type="application/json" id="product-data-json">${dataJson}</script>
          ${card}
        </div>
      </li>`;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN ELEMENT
  // ═══════════════════════════════════════════════════════════════════════════
  class MyAssortment extends HTMLElement {
    constructor() {
      super();
      this._loading = true;
      this.hasError = false;
      this.enableLog = false;
      this.configuration = {};
      /** Full KIOS list (normalised). Master source for filters + counts. */
      this._kios = [];
      /** productId(string) → normalised KIOS entry (for card build + re-render). */
      this._kiosByProductId = new Map();
      /** productId(string) → product JSON (accumulated from cache + API). */
      this._products = new Map();
      this._categories = [];
      this._customTags = [];
      this._activeFilter = null;      // { type:'category'|'custom-tag', key }
      this._mode = 'normal';          // 'normal' (paged over KIOS) | 'filter' (all-at-once)
      this._hasSortIndex = false;     // any KIOS entry manually sorted → sortIndex ordering
      // Which label fills the `.category-title-text` heading:
      //   true  → parent (top-level) category — the default
      //   false → sub-category name
      // This only swaps the heading TEXT; product order (driven by
      // subCategoryIdentifier in _sortKios) is unaffected either way.
      // Overridden from configuration.category_title_type in connectedCallback.
      this._showParentCategoryTitle = true;
      // Whether the per-product custom sort field (.assortment-sort__field) and
      // the "reset sorting" button (data-reset-sort) are shown. Overridden from
      // configuration.enable_custom_sort in connectedCallback.
      this._enableCustomSort = true;
      this._sortedProducts = [];      // ordered [{kioId,variantId,productId,handle,sortOrder}] — full set_assortment_sort payload
      this._sortPositions = null;     // productId(string) → current 1-based position (lazy, see _buildSortPositions)
      this._lastHeader = null;        // last category heading emitted (custom-sort run grouping)
      this._sortDialog = null;        // lazily-built sort dialog controller
      this._confirmDialog = null;     // lazily-built reset-confirmation dialog controller
      // ── last-ordered indicator (theme setting `enable_last_order_indicator`) ──
      // Every field below stays untouched while the feature is off: no parsing,
      // no lookups, no DOM work, no markup.
      this._lastOrderEnabled = false;   // configuration.enable_last_order_indicator === true
      this._lastOrders = null;          // raw `last_orders` object from get_assortment_details
      this._lastOrderProbed = false;    // a dedicated last_orders-only request was already sent
      this._lastOrderKey = '';          // "DDMMYYYY" the index below was built for
      this._lastOrderByVariant = new Map(); // variantId(String) → quantity ordered (O(1) card lookup)
      this._currentPage = 0;          // highest normal page loaded so far
      this._normalExhausted = false;  // reached the end of the KIOS list
      this._anyRendered = false;      // any card rendered yet (drives empty state)
      this._isFetching = false;
      this._handlers = {};
      // Local cart (shopify_local_cart) is the single source of truth for
      // in-cart state. Initialised once here and refreshed on 'cart:updated';
      // reuse this._cart / this._cartQtyByVariant everywhere else.
      this._cart = { items: [] };
      this._cartQtyByVariant = new Map(); // variantId(String) → { quantity, key }
      this._loadCart();
    }

    /**
     * (Re)read `shopify_local_cart` from localStorage into `this._cart` and build
     * the variantId → { quantity, key } lookup. The ONLY place cart data is read;
     * swap the storage/shape here and the rest of the component follows.
     */
    _loadCart() {
      let items = [];
      let attributes = {};
      try {
        const raw = localStorage.getItem('shopify_local_cart');
        if (raw) {
          const parsed = JSON.parse(raw);
          items = Array.isArray(parsed?.items) ? parsed.items : [];
          attributes = parsed?.attributes ?? {};
        }
      } catch (e) { /* ignore malformed cart */ }
      this._cart = { items, attributes };
      // Selected delivery date (cart.attributes.current_date, e.g.
      // "2026-07-17T00:00:00.000Z") → "YYYY-MM-DD". Falls back to the value the
      // section injected, then to today. Drives which price_json row (and which
      // inventory row) the card shows — a blank date resolves no price, so the
      // card would display €0; today's date keeps pricing working.
      const today = todayISO();
      const rawDate = attributes.current_date ?? this.configuration?.current_date ?? '';
      const picked = rawDate ? String(rawDate).slice(0, 10) : today;
      // Never use a past delivery date: its price_json window may have closed,
      // producing stale/€0 prices. Anything earlier than today falls back to
      // today (YYYY-MM-DD strings compare lexicographically).
      this._currentDate = picked < today ? today : picked;
      this._cartQtyByVariant = new Map();
      for (const it of items) {
        const vid = stripVariantGid(it.variant_id ?? it.variantId ?? it.id ?? '');
        if (!vid) continue;
        const prev = this._cartQtyByVariant.get(vid) ?? { quantity: 0, key: null };
        this._cartQtyByVariant.set(vid, {
          quantity: prev.quantity + (Number(it.quantity) || 0),
          key: it.key ?? it.line_item_key ?? prev.key,
        });
      }
    }

    /**
     * Reflect the local cart on every rendered card: set the quantity-input value
     * and toggle the `in-cart` class / `data-line-item` from `_cartQtyByVariant`.
     * @param {ParentNode} [scope] Defaults to the whole component.
     */
    _applyCartState(scope) {
      (scope ?? this).querySelectorAll('quantity-input[data-variant-id]').forEach(qi => {
        const vid = stripVariantGid(qi.dataset.variantId);
        const entry = this._cartQtyByVariant.get(vid);
        const input = qi.querySelector('input[type="number"], .qty-input__input');
        if (entry && entry.quantity > 0) {
          if (input) { input.value = entry.quantity; input.setAttribute('value', String(entry.quantity)); }
          // Sync the quantity-input's internal baseline: its constructor captured
          // currentQty=0 while the input was still empty, so without this a minus
          // click from 1→0 short-circuits (0 === stale currentQty) and never fires.
          qi.currentQty = Number(entry.quantity);
          qi.classList.add('in-cart');
          if (entry.key) qi.setAttribute('data-line-item', entry.key);
        } else {
          if (input) { input.value = ''; input.setAttribute('value', ''); }
          qi.currentQty = 0;
          qi.classList.remove('in-cart');
          qi.removeAttribute('data-line-item');
        }
      });
    }

    // ── lifecycle ────────────────────────────────────────────────────────────
    async connectedCallback() {
      this._dom = {
        products: this.querySelector('[data-products]'),
        saleProducts: this.querySelector('[data-sale-products]'),
        loader: this.querySelector('[data-loader]'),
        content: this.querySelector('[data-content]'),
        empty: this.querySelector('[data-empty]'),
        saleAvail: this.querySelector('[data-sale-available]'),
        saleUnavail: this.querySelector('[data-sale-unavailable]'),
        categoryFilter: this.querySelector('[data-category-filter]'),
        tagFilter: this.querySelector('[data-tag-filter]'),
        tagFilterList: this.querySelector('[data-tag-filter] [render-filters]'),
        tagLoader: this.querySelector('[data-tag-filter] [data-tag-loader]'),
        createFilterButton: this.querySelector('[data-create-filter] button'),
        createFilterInput: this.querySelector('[data-create-filter] input[type="text"]'),
        createFilterColor: this.querySelector('[data-create-filter] input[type="color"]'),
        filterWrap: this.querySelector('.assortment-grid-filter-wrap'),
      };

      try {
        this.configuration = JSON.parse(this.querySelector('[data-configuration]')?.textContent ?? '{}');
        this.enableLog = !!this.configuration.logs_enabled;
        // Theme settings: category title source ('parent' default, else 'sub')
        // and whether the custom sort UI is shown (default true).
        this._showParentCategoryTitle = this.configuration.category_title_type !== 'sub';
        this._enableCustomSort = this.configuration.enable_custom_sort !== false;
        // Last-ordered indicator: opt-in only — anything but an explicit `true`
        // leaves the whole feature dormant (see the constructor block).
        this._lastOrderEnabled = this.configuration.enable_last_order_indicator === true;
        this._normaliseKios(this.configuration.assortment_products ?? []);
        if (this._enableCustomSort) {
          // Seed the manual-sort list (assortment_sorted_products, or derived from
          // KIOS) and merge its orders onto the KIOS entries.
          this._buildSortedProducts();
          // Any manually-assigned sortIndex switches the page to sortIndex ordering
          // (ascending); otherwise the default category/sub-category ordering is
          // kept unchanged. Either way that order IS the list the customer
          // reorders, and every card's input is prefilled with its position in it.
          this._hasSortIndex = this._kios.some(k => k.sortIndex != null);
        } else {
          // Custom sort disabled: drop every manual sortIndex so the grid reverts
          // to the default category/sub-category ordering — no sorted section and
          // no reset button, regardless of what was persisted.
          for (const k of this._kios) k.sortIndex = null;
          this._sortedProducts = [];
          this._hasSortIndex = false;
        }
        this._collectCustomTags(this.configuration.custom_tags ?? []);
        this._loadCart(); // re-read now that config (current_date fallback) is available
      } catch (e) {
        this.hasError = true;
        console.error('[MyAssortment] config parse error:', e);
        return;
      }

      this.customer = {
        id: window.customer?.id ?? null,
        userName: window.customer?.userName ?? null,
        userNumber: window.customer?.currentUserNumber ?? null,
      };

      // this._customTags is mutated in place (never reassigned after init), so
      // passing the array reference keeps the builder's view of tags current.
      this._builder = new CardBuilder({
        customer: this.customer,
        customTags: this._customTags,
        categoryLabel: p => this._categoryLabelFor(p),
        currentDate: () => this._currentDate,
        enableCustomSort: () => this._enableCustomSort,
        sortMax: () => this._sortMax(),
        sortPosition: k => this._sortPositionFor(k),
        lastOrderIndicator: (p, v) => this._lastOrderMarkupFor(p, v),
        inventoryTexts: this.configuration.inventory_texts ?? {},
      });

      // Fire-and-forget cache maintenance (skipped when the kill-switch is on).
      if (!cacheDisabled()) {
        JsonCache.evictExpired().then(n => { if (n && this.enableLog) console.log(`[MyAssortment] evicted ${n} expired`); });
      }

      // Sort KIOS by category priority (same rule as the legacy element).
      this._sortKios();
      this._buildKiosIndex();

      // INSTANT sidebar (no network).
      this._buildCategoriesFromKios();
      this._renderCategorySidebar();
      this._renderTagSidebar();
      this._renderResetSortButton();
      if (window.innerWidth <= 1024) {
        this._dom.tagFilter?.querySelector('ul')?.classList.toggle('visible');
        this._dom.categoryFilter?.querySelector('ul')?.classList.toggle('visible');
      }

      this._injectSortStyles();
      this._attachEventListeners();
      this._createScrollLoader();

      // Reflect the initial loading state → skeleton placeholders in the grid.
      this._toggleLoadingUI();
      // Normal browsing: load server page 1.
      await this._loadNormalPage(1);
      // `last_orders` normally rides along on that response, so the cards built
      // above already carry their badge. A fully-cached page 1 makes no request
      // at all though — then fetch the data on its own and backfill.
      if (this._lastOrderEnabled && this._lastOrders === null) {
        await this._probeLastOrders();
        this._renderMissingLastOrderIndicators();
      }
      this.loading = false;
      this._clearCreateFilterLoading();
      // Initial request returned nothing → no products at all: hide the whole
      // filter/custom-tag sidebar (only the empty notice should remain).
      if (!this._anyRendered) this._dom.filterWrap?.classList.add('hidden');
      this._setupInfiniteScroll();
    }

    /**
     * Clear the spinner on the "add tag" (+) button in the custom-tag panel and
     * enable its text input. The button ships with `is-loading` from Liquid;
     * this removes it once data is ready (initial load, filter load, mode switch).
     */
    _clearCreateFilterLoading() {
      this._dom.createFilterButton?.classList.remove('is-loading');
      this._dom.createFilterInput?.removeAttribute('disabled');
    }

    /**
     * Custom-tag "add" input. Strips any HTML the user pastes/types so a tag
     * name can never carry markup, and enables the "+" button only once at
     * least two characters remain (disabled otherwise).
     */
    _onCreateFilterInput() {
      const input = this._dom.createFilterInput;
      const btn = this._dom.createFilterButton;
      if (!input) return;
      // Remove tag-like sequences and any stray angle brackets — no HTML allowed.
      const clean = input.value.replace(/<[^>]*>/g, '').replace(/[<>]/g, '');
      if (clean !== input.value) input.value = clean;
      if (btn) btn.disabled = clean.trim().length < 2;
    }

    /**
     * Create a custom tag from the "+" button. The tag name is stored as
     * `label:color` (same convention the sidebar splits on). POSTs to
     * set_assortment with `action: "create"`, then — on success — adds the tag
     * to state + sidebar and resets the input.
     */
    async _onCreateFilterClick() {
      const input = this._dom.createFilterInput;
      const colorInput = this._dom.createFilterColor;
      const btn = this._dom.createFilterButton;
      if (!input || !btn || btn.disabled) return;

      // `:` is the label/color delimiter, so it must not appear in the label.
      const label = input.value.replace(/<[^>]*>/g, '').replace(/[<>:]/g, '').trim();
      if (label.length < 2) return;
      const color = colorInput?.value || '#eac2ff';
      const filterName = `${label}:${color}`;

      // Reject duplicates (same label, ignoring colour).
      if (this._customTags.some(t => t.name.split(':')[0] === label)) {
        input.value = '';
        btn.disabled = true;
        return;
      }

      // Lock the UI while the request is in flight.
      btn.disabled = true;
      btn.classList.add('is-loading');
      input.setAttribute('disabled', '');
      colorInput?.setAttribute('disabled', '');

      try {
        const res = await fetch(`${location.origin}${API.SET_ASSORTMENT}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: this.customer.id,
            userName: this.customer.userName,
            userNumber: this.customer.userNumber,
            filterName,
            newFilterName: filterName,
            filterValue: { handle: '', action: null },
            action: 'create',
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        // Add to state and render its sidebar row (before the create-filter row).
        const tag = { name: filterName, handles: [], count: 0, active: false };
        this._customTags.push(tag);
        const createRow = this._dom.tagFilterList?.querySelector('[data-create-filter]');
        if (createRow) {
          createRow.insertAdjacentHTML('beforebegin', this._tagItemMarkup(tag));
          this._wireTagItem(createRow.previousElementSibling);
        }

        document.dispatchEvent(new CustomEvent('on:tag:added', { detail: { filter: filterName } }));
        input.value = '';
      } catch (e) {
        console.error('[MyAssortment] create custom tag failed:', e);
      } finally {
        btn.classList.remove('is-loading');
        input.removeAttribute('disabled');
        colorInput?.removeAttribute('disabled');
        btn.disabled = input.value.trim().length < 2;
      }
    }

    disconnectedCallback() { this._detachEventListeners(); }

    // ── loading UI ────────────────────────────────────────────────────────────
    set loading(v) { this._loading = v; this._toggleLoadingUI(); }
    get loading() { return this._loading; }

    /**
     * Loading is shown as skeleton placeholders — real-looking `.category--title`
     * and `.js-pagination-result` cards injected straight into the grid — instead
     * of the old single `[data-loader]` spinner (now kept permanently hidden). The
     * content wrapper is revealed while loading so the skeletons are visible;
     * `_appendCards()` strips the skeletons the moment real cards arrive.
     */
    _toggleLoadingUI() {
      const { content, loader, empty } = this._dom;
      loader?.classList.add('hidden-i');
      if (this._loading) {
        empty?.classList.add('hidden-i');
        content?.removeAttribute('style');   // reveal the grid so skeletons show
        this._showGridSkeleton();
      } else {
        this._hideGridSkeleton();
        if (this._anyRendered) {
          content?.removeAttribute('style');
          empty?.classList.add('hidden-i');
        } else {
          content?.style.setProperty('display', 'none', 'important');
          empty?.classList.remove('hidden-i');
        }
      }
    }

    /** One skeleton category heading — a `.category--title` look-alike. */
    _skeletonTitleHtml() {
      return `<li class="" data-skeleton aria-hidden="true">
        <h6 class="pt-2 pl-3 pr-3 category-title-text"><span class="skeleton assortment-skeleton__heading"></span></h6>
      </li>`;
    }

    /** One skeleton product card — a `.js-pagination-result` look-alike. */
    _skeletonCardHtml() {
      return `<li class="js-pagination-result assortment-skeleton" data-skeleton aria-hidden="true">
        <div class="assortment-skeleton__card">
          <div class="skeleton assortment-skeleton__media"></div>
          <div class="assortment-skeleton__lines">
            <span class="skeleton" style="--width: 45%;"></span>
            <span class="skeleton" style="--width: 85%;"></span>
            <span class="skeleton" style="--width: 65%;"></span>
            <span class="skeleton assortment-skeleton__price"></span>
          </div>
          <div class="assortment-skeleton__buy">
            <span class="skeleton assortment-skeleton__field"></span>
            <span class="skeleton assortment-skeleton__btn"></span>
          </div>
        </div>
      </li>`;
    }

    /** Inject a heading + a run of skeleton cards into the grid (idempotent). */
    _showGridSkeleton() {
      const grid = this._dom.products;
      if (!grid || grid.querySelector('[data-skeleton]')) return;
      const cards = Array.from({ length: SKELETON_CARD_COUNT }, () => this._skeletonCardHtml()).join('');
      grid.insertAdjacentHTML('beforeend', this._skeletonTitleHtml() + cards);
      this._dom.saleAvail?.classList.remove('hidden');
    }

    /** Remove every skeleton placeholder from the grid. */
    _hideGridSkeleton() {
      this._dom.products?.querySelectorAll('[data-skeleton]').forEach(el => el.remove());
    }

    // ── KIOS normalisation + sorting ───────────────────────────────────────────
    _normaliseKios(list) {
      this._kios = (list ?? []).map(k => ({
        id: k.kioId ?? k.id ?? null,                        // KIOS id → kioIds param (kioId, else id)
        productId: stripProductGid(k.productId),            // numeric shopify product id
        variantId: stripVariantGid(k.variantId),
        handle: k.handle,
        categories: k.categories ?? '',
        subCategoryName: k.subCategoryName ?? '',
        subCategoryIdentifier: k.subCategoryIdentifier ?? '',
        sortIndex: toSortIndex(k.sortIndex),
        sales: k.sales ?? null,
      })).filter(k => k.handle);
    }

    /**
     * Sort the KIOS list by, in order:
     *   1. parent-category priority — lowest index among the entry's categories
     *      in first_level_categories (unknown → Infinity, pushed last);
     *   2. sub-category identifier "S{a}-{b}" ascending (a then b; missing →
     *      Infinity, so unknown sub-categories fall to the end of their parent);
     *   3. original order (stable tiebreak).
     */
    _sortKios() {
      const order = this.configuration?.first_level_categories ?? [];
      if (!order.length && !this._hasSortIndex) return;
      const priority = cats => {
        const list = catList(cats);
        if (!list.length) return Infinity;
        return Math.min(...list.map(c => { const i = order.indexOf(c); return i === -1 ? Infinity : i; }));
      };
      const subKey = ident => {
        const m = /S(\d+)-(\d+)/i.exec(String(ident ?? ''));
        return m ? [Number(m[1]), Number(m[2])] : [Infinity, Infinity];
      };
      // Default (category → sub-category → original) comparator over decorated entries.
      const byDefault = (a, b) =>
        a.p !== b.p ? a.p - b.p :
        a.s[0] !== b.s[0] ? a.s[0] - b.s[0] :
        a.s[1] !== b.s[1] ? a.s[1] - b.s[1] :
        a.i - b.i;

      const decorated = this._kios.map((k, i) => ({ k, p: priority(k.categories), s: subKey(k.subCategoryIdentifier), i }));

      if (this._hasSortIndex) {
        // Manually-sorted products first, by sortIndex ascending; everything else
        // keeps the default category ordering after them. Server-side reindexing
        // guarantees the sortIndex values are unique, so ties never occur, but the
        // original index is used as a stable final tiebreak regardless.
        decorated.sort((a, b) => {
          const ai = a.k.sortIndex, bi = b.k.sortIndex;
          const aHas = ai != null, bHas = bi != null;
          if (aHas && bHas) return ai !== bi ? ai - bi : a.i - b.i;
          if (aHas !== bHas) return aHas ? -1 : 1;
          return byDefault(a, b);
        });
      } else {
        decorated.sort(byDefault);
      }
      this._kios = decorated.map(x => x.k);
    }

    /** productId → KIOS entry lookup, used to build cards for server-paged results. */
    _buildKiosIndex() {
      this._kiosByProductId = new Map(this._kios.map(k => [String(k.productId), k]));
      // Positions follow the KIOS order/content — drop them so the next card
      // render rebuilds them from the current list.
      this._sortPositions = null;
    }

    // ── sort positions (prefilled into every [data-sort-input]) ────────────────
    /**
     * Whether a KIOS entry takes part in the numbering — i.e. whether its product
     * is orderable and therefore sits in the main grid. Not-orderable products are
     * rendered into `[data-sale-unavailable]` instead, so they are skipped: the
     * positions the customer sees and types are the main grid's own 1, 2, 3, … .
     *
     * A product whose JSON has not been fetched yet counts as orderable. Its
     * availability is simply unknown, and a position only depends on the entries
     * BEFORE it — all of which are loaded by the time a card renders, since pages
     * load in order. Assuming the other way would renumber cards already on screen
     * as the tail arrives.
     */
    _isOrderableKio(kiosEntry) {
      const product = this._products.get(String(kiosEntry?.productId));
      if (!product) return true;
      return resolveVariant(product).orderable;
    }

    /**
     * Upper bound of a valid position — the number of products the customer can
     * actually reorder, i.e. the orderable entries of the assortment (which is
     * exactly what the position map holds).
     */
    _sortMax() {
      const map = this._sortPositions ?? this._buildSortPositions();
      return Math.max(map.size, 1);
    }

    /**
     * Build the `productId → position` map: the KIOS list in its CURRENT display
     * order, numbered 1, 2, 3, … M over the ORDERABLE entries only. Not-orderable
     * products are left out of the map (they live in the separate unavailable
     * block and get no sort input), so the numbering stays dense and gap-free:
     *
     *   list  P1, P2(N), P3, P4(N), P5   →   main grid  P1 #1, P3 #2, P5 #3
     *
     * A persisted sortIndex only decides where a product lands in that order (see
     * _sortKios), it is never shown as-is — which keeps the numbering unique and
     * contiguous even when the backend seed is sparse or stale. Built lazily and
     * invalidated whenever the KIOS list changes (_buildKiosIndex) and rebuilt on
     * every render (_appendCards), since each batch of loaded products can resolve
     * the availability of more entries.
     */
    _buildSortPositions() {
      const map = new Map();
      let position = 0;
      for (const k of this._kios) {
        if (!this._isOrderableKio(k)) continue;
        map.set(String(k.productId), ++position);
      }
      this._sortPositions = map;
      return map;
    }

    /**
     * Current 1-based position of one KIOS entry in the main grid — null when it
     * is not in the list, or not orderable (no position of its own).
     */
    _sortPositionFor(kiosEntry) {
      const map = this._sortPositions ?? this._buildSortPositions();
      return map.get(String(kiosEntry?.productId)) ?? null;
    }

    /**
     * 1-based position of one KIOS entry in the FULL list, not-orderable entries
     * included. This is the numbering the backend persists (`set_assortment_sort`
     * / `create_kio`'s `sortOrder`), as opposed to the orderable-only numbering
     * shown on the cards. Null when the entry is not in the list.
     */
    _fullListPositionFor(kiosEntry) {
      if (!kiosEntry) return null;
      const i = this._kios.findIndex(k => String(k.productId) === String(kiosEntry.productId));
      return i === -1 ? null : i + 1;
    }

    // ── sidebar: categories ────────────────────────────────────────────────────
    _buildCategoriesFromKios() {
      const { first_level_categories = [], category_language_texts = {} } = this.configuration;
      const counts = new Map();
      for (const k of this._kios) {
        for (const c of catList(k.categories)) {
          if (first_level_categories.includes(c) && c !== 'Aktuell') {
            counts.set(c, (counts.get(c) ?? 0) + 1);
          }
        }
      }
      const activeSet = new Set(this._categories.filter(c => c.active).map(c => c.tag));
      this._categories = [...counts.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([code, count]) => ({
          tag: code, label: category_language_texts[code] ?? code, count, active: activeSet.has(code),
        }));
    }

    _categoryLabelFor(product) {
      const { first_level_categories = [], category_language_texts = {} } = this.configuration;
      const codes = (product.tags ?? [])
        .filter(t => t.startsWith('Category:'))
        .map(t => t.replace('Category:', ''))
        .filter(c => first_level_categories.includes(c) && c !== 'Aktuell')
        .sort((a, b) => a.localeCompare(b));
      return codes.map(c => category_language_texts[c] ?? c).join(', ');
    }

    /**
     * Top-level category header label for a KIOS entry, built from its
     * `categories` + `first_level_categories` + `category_language_texts` (per
     * spec). When the entry belongs to several top-level categories the one
     * ranked earliest in `first_level_categories` wins, so "Aktuell" (ranked
     * last) never masks a real category. Used to group the sortIndex view.
     */
    _parentCategoryLabel(kiosEntry) {
      const { first_level_categories = [], category_language_texts = {} } = this.configuration;
      const codes = catList(kiosEntry?.categories)
        .filter(c => first_level_categories.includes(c))
        .sort((a, b) => first_level_categories.indexOf(a) - first_level_categories.indexOf(b));
      if (codes.length) return category_language_texts[codes[0]] ?? codes[0];
      // Fall back to the product-tag derived label (may be empty).
      return this._categoryLabelFor(this._products.get(String(kiosEntry?.productId)) ?? {});
    }

    _renderCategorySidebar() {
      const ul = this._dom.categoryFilter?.querySelector('ul');
      if (!ul) return;
      ul.innerHTML = this._categories.filter(c => c.count > 0).map(c => `
        <li class="pl-6 flex justify-between items-center cursor-pointer relative${c.active ? ' active' : ''}" data-filter-item data-filter-type="category" data-filter="${escAttr(c.tag)}" data-count="${c.count}">
          <span class="flex gap-2 items-center">${escHTML(c.label)}</span>
          <div data-count-display style="color: rgba(var(--text-color) / .7);">${addNumberPrefix(c.count)}</div>
        </li>`).join('');
      ul.querySelectorAll('[data-filter-item]').forEach(el => el.addEventListener('click', () => this._onFilterClick(el)));
    }

    // ── sidebar: custom tags ────────────────────────────────────────────────────
    _collectCustomTags(list) {
      this._customTags = (list ?? []).map(t => ({
        name: t.name,
        handles: (t.handles ?? []).filter(h => h?.trim()),
        count: (t.handles ?? []).filter(h => h?.trim()).length,
        active: false,
      }));
    }

    /** Markup for a single custom-tag sidebar `<li>` (name = "label:color"). */
    _tagItemMarkup(t) {
      const [label, color] = t.name.split(':');
      return `<li class="pl-6 flex justify-between items-center relative ${t.count === 0 ? 'disabled-tag' : 'cursor-pointer'}" data-filter-item data-filter-type="custom-tag" data-filter="${escAttr(t.name)}" data-count="${t.count}">
          <span class="flex gap-3 items-center">
            <span data-delete-custom-tag>${SVG.delete}</span>
            <div class="badge-item"><span style="background-color:${escAttr(color)};">${escHTML(label)}</span></div>
          </span>
          <div data-count-display style="color: rgba(var(--text-color) / .7);">${addNumberPrefix(t.count)}</div>
        </li>`;
    }

    /** Attach the filter (badge) + delete listeners to one custom-tag `<li>`. */
    _wireTagItem(li) {
      if (!li) return;
      li.querySelector('.badge-item')?.addEventListener('click', () => this._onFilterClick(li));
      li.querySelector('[data-delete-custom-tag]')?.addEventListener('click', e => {
        e.stopPropagation();
        this._onDeleteCustomTag(li);
      });
    }

    _renderTagSidebar() {
      const list = this._dom.tagFilterList;
      if (!list) return;
      const markup = this._customTags.map(t => this._tagItemMarkup(t)).join('');
      list.insertAdjacentHTML('afterbegin', markup);
      this._dom.tagLoader?.classList.add('hidden');
      list.querySelectorAll('[data-filter-item]').forEach(li => this._wireTagItem(li));
    }

    /**
     * Delete a custom tag (the trash icon). Mirrors the legacy element:
     * POSTs set_assortment with `action: "remove"`, then — on success — strips
     * the tag's badges from any visible cards, removes it from state + sidebar,
     * notifies listeners, and drops back to normal browsing if it was active.
     */
    async _onDeleteCustomTag(li) {
      if (!li) return;
      const name = li.dataset.filter;
      const tag = this._customTags.find(t => t.name === name);
      if (!tag) return;

      const btn = this._dom.createFilterButton;
      const input = this._dom.createFilterInput;
      const colorInput = this._dom.createFilterColor;

      // Lock the create-filter row while the request is in flight.
      btn?.classList.add('is-loading');
      if (btn) btn.disabled = true;
      input?.setAttribute('disabled', '');
      colorInput?.setAttribute('disabled', '');

      try {
        const res = await fetch(`${location.origin}${API.SET_ASSORTMENT}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: this.customer.id,
            userName: this.customer.userName,
            userNumber: this.customer.userNumber,
            filterName: name,
            filterValue: { handle: tag.handles, action: null },
            action: 'remove',
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const handles = [...tag.handles];
        const wasActive = tag.active || this._activeFilter?.key === name;

        // Remove from state + sidebar. Splice in place (never reassign) so the
        // CardBuilder — which holds a reference to this array — stops rendering
        // the deleted tag on any card it builds afterwards.
        const idx = this._customTags.findIndex(t => t.name === name);
        if (idx !== -1) this._customTags.splice(idx, 1);
        li.remove();

        // Strip the tag's badge off any live cards and notify listeners.
        handles.forEach(handle => {
          this._updateBadge(handle, name, 'remove');
          document.dispatchEvent(new CustomEvent('on:product-assortment:remove', { detail: { handle, tag: name } }));
        });
        document.dispatchEvent(new CustomEvent('on:tag:removed', { detail: { tag: name } }));

        // If the deleted tag was the active filter, fall back to normal browsing.
        if (wasActive) {
          this.querySelectorAll('[data-filter-item]').forEach(el => el.classList.remove('active'));
          this._categories.forEach(c => (c.active = false));
          this._customTags.forEach(t => (t.active = false));
          this._activeFilter = null;
          this._switchToNormal();
        }
      } catch (e) {
        console.error('[MyAssortment] delete custom tag failed:', e);
      } finally {
        btn?.classList.remove('is-loading');
        input?.removeAttribute('disabled');
        colorInput?.removeAttribute('disabled');
        if (btn) btn.disabled = (input?.value.trim().length ?? 0) < 2;
      }
    }

    // ── filtering ────────────────────────────────────────────────────────────
    /**
     * Toggle a category / custom-tag filter.
     *   • Activate  → switch to FILTER mode: gather the filter's KIOS ids
     *                 (category) or handles (custom-tag), cache-check per product,
     *                 fetch only the missing ones, then render ALL matches at once
     *                 (no infinite scroll — per spec).
     *   • De-select → switch back to NORMAL mode: re-render the already-loaded
     *                 pages straight from cache (never re-hits the API).
     */
    async _onFilterClick(item) {
      if (!item) return;
      const { filter, filterType } = item.dataset;
      const isActive = item.classList.contains('active');
      this.querySelectorAll('[data-filter-item]').forEach(el => el.classList.remove('active'));
      this._categories.forEach(c => (c.active = false));
      this._customTags.forEach(t => (t.active = false));

      if (isActive) {
        this._activeFilter = null;
        this._switchToNormal();
        return;
      }

      // Entering a filter from normal browsing — remember the scroll position so
      // we can drop the user back exactly where they were when the filter closes.
      if (this._mode !== 'filter') this._normalScrollY = window.scrollY;

      // Resolve the matching KIOS entries for this filter.
      let entries;
      if (filterType === 'category') {
        const cat = this._categories.find(c => c.tag === filter);
        if (!cat?.count) return;
        cat.active = true;
        entries = this._kios.filter(k => catList(k.categories).includes(filter));
      } else {
        const tag = this._customTags.find(t => t.name === filter);
        if (!tag?.count) return;
        tag.active = true;
        const handleSet = new Set(tag.handles);
        entries = this._kios.filter(k => handleSet.has(k.handle));
      }
      item.classList.add('active');
      this._activeFilter = { type: filterType, key: filter };
      this._mode = 'filter';
      await this._loadFilter(entries, filterType);

      const top = this._dom.saleAvail?.getBoundingClientRect().top ?? 100;
      window.scrollTo({ top: window.scrollY + top - 20, behavior: 'smooth' });
    }

    /**
     * Re-render every already-loaded normal page from cache — no API call.
     * Used when de-selecting a filter (mode switch back to normal). Infinite
     * scroll resumes from `_currentPage + 1` (the first page never loaded yet).
     */
    _switchToNormal() {
      this._mode = 'normal';
      this._clearGrid();
      // Re-render every normal page loaded so far, straight from cache/memory.
      const upto = this._kios.slice(0, Math.max(this._currentPage, 1) * PAGE_SIZE);
      if (upto.length) this._appendCards(upto);
      this._clearCreateFilterLoading();
      // Restore the pre-filter scroll position once the grid is laid out, so the
      // user continues browsing from where they left off.
      if (this._normalScrollY != null) {
        const y = this._normalScrollY;
        requestAnimationFrame(() => window.scrollTo(0, y));
      }
    }

    /**
     * FILTER mode loader. Cache-checks the filter's products (memory → IDB) and
     * fetches only the missing ones in a single request whose `per_page` equals
     * the total filter count, then renders every matched product at once.
     *
     * @param {object[]} entries    KIOS entries matching the filter.
     * @param {string}   filterType 'category' | 'custom-tag'.
     */
    async _loadFilter(entries, filterType) {
      if (this._isFetching) return;
      this._isFetching = true;
      this._clearGrid();        // clear first so the skeletons below aren't wiped
      this.loading = true;      // → injects skeleton placeholders into the empty grid
      this._setScrollLoader(true);
      try {
        const total = entries.length;

        // 1. In-memory (already loaded this session).
        let need = entries.filter(k => !this._products.has(String(k.productId)));

        // 2. IndexedDB — check by the SAME key the API would use:
        //    category → kioId, custom-tag → handle. Only what memory lacked.
        if (need.length && !cacheDisabled()) {
          if (filterType === 'custom-tag') {
            const cached = await JsonCache.getByHandles(need.map(k => k.handle).filter(Boolean));
            for (const k of need) { const d = cached.get(String(k.handle)); if (d) this._products.set(String(k.productId), d); }
          } else {
            const cached = await JsonCache.getByKioIds(need.map(k => k.id).filter(v => v != null));
            for (const k of need) { const d = cached.get(String(k.id)); if (d) this._products.set(String(k.productId), d); }
          }
        }

        // 3. API for ONLY the still-missing products (per_page = total filter count).
        const missing = entries.filter(k => !this._products.has(String(k.productId)));
        if (missing.length) {
          const fetched = await this._fetchFilterProducts(missing, filterType, total);
          this._cacheFetched(fetched);
        }

        // 4. Render the whole filtered set at once (cached + freshly fetched).
        this._appendCards(entries);
      } catch (e) {
        console.error('[MyAssortment] loadFilter failed:', e);
      } finally {
        this._setScrollLoader(false);
        this.loading = false;
        this._isFetching = false;
        this._clearCreateFilterLoading();
      }
    }

    /**
     * Merge fetched products into memory + IndexedDB, tagging each cache record
     * with its kioId + handle (from the KIOS index) so it is findable all three
     * ways later.
     * @param {Map<string, object>} fetched  productId → product JSON
     */
    _cacheFetched(fetched) {
      const writes = [];
      for (const [pid, data] of fetched) {
        this._products.set(String(pid), data);
        const k = this._kiosByProductId.get(String(pid));
        writes.push({ productId: pid, kioId: k?.id ?? null, handle: k?.handle ?? data.handle ?? null, data });
      }
      if (writes.length && !cacheDisabled()) JsonCache.setMany(writes).catch(() => {});
    }

    /**
     * Fetch the missing products of a filter in one request. IDs/handles go as
     * URL query params (array form): category → `kioIds[]`, custom-tag →
     * `handles[]`. `per_page` = TOTAL filter count so everything returns at once.
     * @returns {Promise<Map<string, object>>} productId → product JSON
     */
    async _fetchFilterProducts(missing, filterType, total) {
      if (!missing.length) return new Map();
      const opts = { page: 1, per_page: total || missing.length };
      if (filterType === 'custom-tag') opts.handles = missing.map(k => k.handle).filter(Boolean);
      else opts.kioIds = missing.map(k => k.id).filter(v => v != null);
      return this._postDetails(opts);
    }

    /**
     * Shared POST to get_assortment_details. `page`/`per_page` and any
     * `kioIds`/`handles` arrays go on the URL as query params (array form);
     * customer identity goes in the POST body. Returns Map<productId, JSON>
     * preserving response order.
     */
    async _postDetails({ page = 1, per_page = PAGE_SIZE, kioIds = null, handles = null } = {}) {
      const out = new Map();
      try {
        // Everything goes in the POST body so arrays reach the middleware as real
        // arrays ([12,35,…] / ["abc","xyz"]) — query strings arrive stringified
        // ("[12,35]") and the server can't parse them.
        const body = {
          customerId: this.customer.id,
          userName: this.customer.userName,
          userNumber: this.customer.userNumber,
          page,
          per_page,
        };
        if (kioIds) body.kioIds = kioIds.map(Number).filter(Number.isFinite);
        if (handles) body.handles = handles.map(String).filter(Boolean);

        const res = await fetch(`${location.origin}${API.GET_DETAILS}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        // `last_orders` rides along on the response; fold it into the variant
        // index BEFORE the caller builds any card from `out`, so those cards get
        // their badge inline. If this response is what first made the data known
        // (or changed it), cards already on screen are backfilled too — the guard
        // in _renderMissingLastOrderIndicators keeps that from double-rendering.
        if (this._lastOrderEnabled && this._captureLastOrders(json)) {
          this._renderMissingLastOrderIndicators();
        }
        for (const row of json?.data ?? []) {
          const product = row.product ?? row;
          if (product?.id != null) out.set(String(product.id), product);
        }
        if (this.enableLog) console.log(`[MyAssortment] fetched ${out.size} (page ${page}, per_page ${per_page})`);
      } catch (e) {
        console.error('[MyAssortment] get_assortment_details failed:', e);
      }
      return out;
    }

    // ── last-ordered indicator ────────────────────────────────────────────────
    /**
     * Take the `last_orders` object off a get_assortment_details response and
     * (re)build the variant index from it. Ignores responses that carry no
     * `last_orders` key, so a partial/odd response never wipes a good index.
     * @returns {boolean} whether the rendered indicators need refreshing.
     */
    _captureLastOrders(json) {
      const raw = json?.last_orders;
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
      this._lastOrders = raw;
      return this._buildLastOrderIndex();
    }

    /**
     * Fold `last_orders[DDMMYYYY-of-selected-delivery-date]` into
     * `variantId → quantity`. Everything the cards need is resolved ONCE here, so
     * building a card costs a single Map.get — no scanning of the orders array and
     * no scanning of the grid. A date with no bucket simply yields an empty Map
     * (nothing is rendered, per spec).
     * @returns {boolean} whether the resulting index differs from the previous one.
     */
    _buildLastOrderIndex() {
      const key = toOrderDateKey(this._currentDate);
      const rows = key && this._lastOrders ? this._lastOrders[key] : null;
      const map = new Map();
      if (Array.isArray(rows)) {
        for (const row of rows) {
          const vid = stripVariantGid(row?.variant_id ?? row?.variantId ?? '');
          const qty = Number(row?.quantity);
          if (!vid || !isFinite(qty) || qty <= 0) continue;
          // Same variant listed twice (split order lines) → show the total.
          map.set(vid, (map.get(vid) ?? 0) + qty);
        }
      }
      const changed = key !== this._lastOrderKey
        || map.size !== this._lastOrderByVariant.size
        || [...map].some(([vid, qty]) => this._lastOrderByVariant.get(vid) !== qty);
      this._lastOrderKey = key;
      this._lastOrderByVariant = map;
      return changed;
    }

    /**
     * Indicator markup for a product: one badge per variant of it that was ordered
     * for the selected date, with only `selectedVariant`'s badge visible. Returns
     * '' when the feature is off or no variant was ordered. Called by the
     * CardBuilder while a card's HTML is assembled — one Map lookup per variant,
     * zero DOM access.
     *
     * Emitting the whole set up front is what lets the variant picker swap badges
     * on selection (VariantPicker.updateOrderTruck) with a pure class toggle.
     */
    _lastOrderMarkupFor(product, selectedVariant) {
      if (!this._lastOrderEnabled || !this._lastOrderByVariant.size) return '';
      const selectedId = stripVariantGid(selectedVariant?.id);
      const isoDate = `${this._currentDate}T00:00:00.000Z`;
      const displayDate = toGermanDate(this._currentDate);
      let html = '';
      for (const v of product?.variants ?? []) {
        const vid = stripVariantGid(v?.id);
        const qty = vid ? this._lastOrderByVariant.get(vid) : undefined;
        if (qty == null) continue;
        html += lastOrderIndicatorHtml(vid, qty, isoDate, displayDate, vid !== selectedId);
      }
      return html;
    }

    /**
     * variantId → productId over the products currently in memory. Only built for
     * the backfill path (below), which is rare — the inline builder never needs it
     * because it already holds the product.
     */
    _buildVariantToProductIndex() {
      const map = new Map();
      for (const [pid, product] of this._products) {
        for (const v of product?.variants ?? []) {
          const vid = stripVariantGid(v?.id);
          if (vid) map.set(vid, String(pid));
        }
      }
      return map;
    }

    /**
     * One dedicated get_assortment_details call made ONLY to obtain `last_orders`
     * (`per_page: 1` — the returned product is discarded and never cached). Needed
     * when the initial page was served entirely from IndexedDB, so no request that
     * would have carried the data was ever made. Runs at most once per page view.
     */
    async _probeLastOrders() {
      if (!this._lastOrderEnabled || this._lastOrders !== null || this._lastOrderProbed) return;
      this._lastOrderProbed = true;
      await this._postDetails({ page: 1, per_page: 1 });
    }

    /**
     * Backfill indicators onto cards that were rendered BEFORE `last_orders` was
     * known (fully-cached first page) or whose badge changed with the delivery
     * date. Driven by the index — one attribute-selector lookup per ORDERED item,
     * never a walk over the grid — and skips cards that already carry a badge.
     * @param {ParentNode} [scope] Defaults to the whole component.
     */
    _renderMissingLastOrderIndicators(scope) {
      if (!this._lastOrderEnabled || !this._lastOrderByVariant.size) return;
      const root = scope ?? this;
      const isoDate = `${this._currentDate}T00:00:00.000Z`;
      const displayDate = toGermanDate(this._currentDate);

      // Group the ordered variants by the product that owns them, so a card with
      // several ordered variants is located once and gets its whole badge set.
      const productOf = this._buildVariantToProductIndex();
      const byProduct = new Map();
      for (const vid of this._lastOrderByVariant.keys()) {
        const pid = productOf.get(vid);
        if (!pid) continue;
        if (!byProduct.has(pid)) byProduct.set(pid, []);
        byProduct.get(pid).push(vid);
      }

      for (const [pid, vids] of byProduct) {
        root.querySelectorAll(`.js-pagination-result[data-product-id="${CSS.escape(pid)}"]`).forEach(li => {
          const host = li.querySelector('.product-info__block-buy-buttons');
          const form = host?.querySelector('product-form');
          if (!form) return;
          // <assortment-interact> carries the LIVE variant id (variant-picker.js
          // rewrites it on change), so it is the truth for "which badge shows".
          const selectedId = stripVariantGid(
            li.querySelector('assortment-interact')?.dataset.variantId
            ?? li.querySelector('variant-picker')?.dataset.currentVariant
            ?? ''
          );
          for (const vid of vids) {
            if (host.querySelector(`.order-truck-container[data-varinat-id="${CSS.escape(vid)}"]`)) continue;
            const qty = this._lastOrderByVariant.get(vid);
            form.insertAdjacentHTML('beforebegin', lastOrderIndicatorHtml(vid, qty, isoDate, displayDate, vid !== selectedId));
          }
        });
      }
    }

    /**
     * The selected delivery date changed → the whole badge set is stale. Rebuild
     * the index for the new date, drop every rendered badge, and re-add the ones
     * that still apply. No-op unless the index actually changed.
     */
    _refreshLastOrderIndicators() {
      if (!this._lastOrderEnabled) return;
      if (!this._buildLastOrderIndex()) return;
      this.querySelectorAll('.order-truck-container').forEach(el => el.remove());
      this._renderMissingLastOrderIndicators();
    }

    /**
     * NORMAL mode, page `pageNum` (1-based) = a 100-item slice of the KNOWN KIOS
     * list. Cache-first per product (memory → IDB by product id), then the API is
     * asked ONLY for the slice's missing products — sent as `kioIds` so the
     * backend returns just those, never the whole assortment. This is why a page
     * refresh re-fetches only removed/missing products, not everything.
     */
    async _loadNormalPage(pageNum) {
      if (this._isFetching) return;
      this._isFetching = true;
      this._setScrollLoader(true);
      try {
        const start = (pageNum - 1) * PAGE_SIZE;
        const slice = this._kios.slice(start, start + PAGE_SIZE);
        if (!slice.length) { this._normalExhausted = true; return; }

        // 1. In-memory, then 2. IDB by product id.
        let need = slice.filter(k => !this._products.has(String(k.productId)));
        if (need.length && !cacheDisabled()) {
          const cached = await JsonCache.getByProductIds(need.map(k => k.productId).filter(Boolean));
          for (const [pid, data] of cached) this._products.set(String(pid), data);
        }

        // 3. API for only the still-missing products, by their KIOS ids.
        const missing = slice.filter(k => !this._products.has(String(k.productId)));
        if (missing.length) {
          const fetched = await this._postDetails({ page: 1, per_page: PAGE_SIZE, kioIds: missing.map(k => k.id).filter(v => v != null) });
          this._cacheFetched(fetched);
        }

        this._appendCards(slice);
        this._currentPage = Math.max(this._currentPage, pageNum);
        if (start + PAGE_SIZE >= this._kios.length) this._normalExhausted = true;
      } catch (e) {
        console.error(`[MyAssortment] loadNormalPage(${pageNum}) failed:`, e);
      } finally {
        this._setScrollLoader(false);
        this._isFetching = false;
      }
    }

    // ── rendering ────────────────────────────────────────────────────────────
    /** Empty both grids (available + unavailable). */
    _clearGrid() {
      if (this._dom.products) this._dom.products.innerHTML = '';
      if (this._dom.saleProducts) this._dom.saleProducts.innerHTML = '';
      this._anyRendered = false;
      this._lastHeader = null;
    }

    _appendCards(kiosSlice) {
      this._hideGridSkeleton();   // real cards replace any loading placeholders
      // Availability decides which entries are numbered, and it is only known once
      // a product's JSON is in memory — so rebuild the position map from what is
      // known NOW (products arrive from four paths: memory, IDB, and two fetches).
      this._sortPositions = null;
      // Two grids: orderable products keep the main `[data-products]` list,
      // not-orderable ones go to the collapsed `[data-sale-unavailable]` block
      // (their `<li>` also carries `.assortment-item--unavailable`). Because they
      // leave the main list, they take no position with them — the numbering
      // counts orderable products only (_buildSortPositions), so the main grid
      // stays 1, 2, 3, … with no gaps where an unavailable product used to sit.
      const availableHtml = [];
      const unavailableHtml = [];
      // Category headings. Two grouping rules, picked by the custom-sort flag:
      //   • ON — the grid is ONE list the customer reorders freely, so each
      //     product's own parent/sub-category grouping travels WITH it: a heading
      //     is emitted whenever the label differs from the previous card's, down
      //     the list. Moving a product into the middle of another category prints
      //     its own heading there and re-prints the interrupted one for the cards
      //     that follow, so the category/sub-category structure itself is never
      //     rewritten — only the positions change. The run continues across pages
      //     (and across a prune) by reading the last heading already in the grid.
      //   • OFF — unchanged default behaviour: the KIOS list is in
      //     category/sub-category order, so each heading is emitted once and
      //     deduped across the whole grid.
      // Both hold identically in the normal, category-filter and custom-tag views.
      const runMode = this._enableCustomSort;
      const headings = this._dom.products.querySelectorAll('.category--title');
      const seenCats = runMode ? null : new Set([...headings].map(el => el.dataset.category));
      if (runMode) this._lastHeader = headings.length ? headings[headings.length - 1].dataset.category : null;

      for (const k of kiosSlice) {
        const product = this._products.get(String(k.productId));
        if (!product) continue;
        const { orderable } = resolveVariant(product);
        // Group heading = sub-category name; parent category kept alongside.
        const parentLabel = this._categoryLabelFor(product);
        const subName = k.subCategoryName || parentLabel;
        // Heading text follows the _showParentCategoryTitle toggle. The parent
        // label is kept alongside in data-parent-category regardless, so nothing
        // downstream loses the top-level category when sub-category mode is on.
        const parentHeading = this._parentCategoryLabel(k) || parentLabel;
        const header = this._showParentCategoryTitle ? parentHeading : (subName || parentHeading);
        const cardHtml = this._builder.build(product, k, { parentLabel, subName });
        if (!cardHtml) continue;

        if (orderable) {
          if (header && (runMode ? header !== this._lastHeader : !seenCats.has(header))) {
            if (runMode) this._lastHeader = header;
            else seenCats.add(header);
            availableHtml.push(`<li class="category--title" data-category="${escAttr(header)}" data-parent-category="${escAttr(parentHeading)}"><h6 class="pt-2 pl-3 pr-3 category-title-text">${escHTML(header)}</h6></li>`);
          }
          availableHtml.push(cardHtml);
        } else {
          unavailableHtml.push(cardHtml);
        }
      }

      if (availableHtml.length) {
        this._dom.products.insertAdjacentHTML('beforeend', availableHtml.join(''));
        this._dom.saleAvail?.classList.remove('hidden');
      }
      if (unavailableHtml.length) {
        this._dom.saleProducts.insertAdjacentHTML('beforeend', unavailableHtml.join(''));
        this._dom.saleUnavail?.classList.remove('hidden');
      }
      if (availableHtml.length || unavailableHtml.length) this._anyRendered = true;

      this._applyCartState();
      this._applyTabIndexes();
      // Wire follow-up swap buttons once each.
      this.querySelectorAll('.card--product__change-assortment-product').forEach(btn => {
        if (btn._swapBound) return;
        btn._swapBound = true;
        btn.addEventListener('click', () => this._changeFollowUp(btn));
      });
    }

    /**
     * "Artikel tauschen" — swap an out-of-stock product for its follow-up:
     * move custom tags across, swap the KIOS variant, then drop the current
     * product from the view and re-render.
     *
     * The follow-up inherits the position the swapped-out product holds, so the
     * replacement lands exactly where the old product sat instead of at the end
     * of the assortment: CREATE_KIO is sent an extra integer `sortOrder`. That is
     * the FULL-list position (_fullListPositionFor) — the numbering the backend
     * persists — not a main-grid position: the product being swapped out is by
     * definition not orderable, so it has no main-grid position of its own.
     */
    async _changeFollowUp(btn) {
      if (!btn) return;
      btn.classList.add('btn--loading');
      btn.setAttribute('disabled', '');
      const { tags } = JSON.parse(btn.querySelector('[data-product-tags]')?.textContent ?? '{"tags":[]}');
      const { newVariantId, newHandle, newVariantCategory, newProductId, currentProductId, currentVariantId, currentHandle } = btn.dataset;
      const params = { customerId: this.customer.id, userName: this.customer.userName, userNumber: this.customer.userNumber };
      const post = (url, body) => fetch(`${location.origin}${url}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      // Position of the product being swapped out — handed to the follow-up.
      const currentKio = this._kiosByProductId.get(String(currentProductId));
      const sortOrder = this._fullListPositionFor(currentKio) ?? toSortIndex(currentKio?.sortIndex);

      try {
        const fd = new FormData();
        fd.set('customerId', params.customerId ?? '');
        fd.set('userNumber', params.userNumber ?? '');
        fd.set('userName', params.userName ?? '');
        fd.set('variantId', `gid://shopify/ProductVariant/${newVariantId}`);
        if (sortOrder != null) fd.set('sortOrder', String(sortOrder));
        await fetch(API.CREATE_KIO, { method: 'POST', body: fd });
        if (this.enableLog) console.log(`[MyAssortment] follow-up swap → create_kio sortOrder #${sortOrder ?? '—'}`);
        fd.delete('sortOrder');   // delete_kio takes the identity fields only
        fd.set('variantId', `gid://shopify/ProductVariant/${currentVariantId}`);
        await fetch(API.DELETE_KIO, { method: 'POST', body: fd });
      } catch (e) {
        console.error('[MyAssortment] follow-up swap failed:', e);
      }

      // Replace the current KIOS entry with the follow-up, drop its cache, then
      // rebuild the KIOS list + sidebar and reload normal browsing from page 1.
      await JsonCache.del(String(currentProductId)).catch(() => {});
      this._products.delete(String(currentProductId));
      window.location.reload();
    }

    // ── infinite scroll (trigger = the page footer .cc-footer) ───────────────────
    /**
     * Load the next batch as the footer approaches. Observes `.cc-footer` with a
     * generous rootMargin so the fetch starts before it is fully visible; falls
     * back to a scroll listener where IntersectionObserver is unavailable.
     */
    _setupInfiniteScroll() {
      const footer = document.querySelector('.cc-footer');

      if (footer && 'IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(entries => {
          if (entries.some(e => e.isIntersecting)) this._maybeLoadMore();
        }, { rootMargin: `${INFINITE_SCROLL_OFFSET}px 0px` });
        this._observer.observe(footer);
        return;
      }

      // Fallback: proximity to the footer (or document bottom) on scroll.
      this._handlers.scroll = () => {
        const trigger = footer
          ? footer.getBoundingClientRect().top - window.innerHeight
          : document.body.offsetHeight - (window.innerHeight + window.scrollY);
        if (trigger <= INFINITE_SCROLL_OFFSET) this._maybeLoadMore();
      };
      window.addEventListener('scroll', this._handlers.scroll, { passive: true });
    }

    /**
     * Footer reached. Only NORMAL mode paginates (filter mode shows everything
     * at once). Loads the next server page, then keeps going while the footer is
     * still in the trigger zone and pages remain (one page may not fill a tall
     * viewport, and the observer would not re-fire until the next scroll).
     */
    async _maybeLoadMore() {
      if (this._mode !== 'normal') return;
      if (this._isFetching || this._normalExhausted) return;
      await this._loadNormalPage(this._currentPage + 1);
      const footer = document.querySelector('.cc-footer');
      if (footer && !this._normalExhausted) {
        const distance = footer.getBoundingClientRect().top - window.innerHeight;
        if (distance <= INFINITE_SCROLL_OFFSET) requestAnimationFrame(() => this._maybeLoadMore());
      }
    }

    // ── scroll loader (appears under the last card while fetching) ───────────────
    _createScrollLoader() {
      const el = document.createElement('div');
      el.className = 'my-assortment__more-loader flex flex-col items-center hidden';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = '<div class="loader"></div>';
      this._dom.saleAvail?.appendChild(el);
      this._dom.scrollLoader = el;
    }

    /** Show/hide the after-last-card loader. Always removed on response/empty/fail. */
    _setScrollLoader(on) {
      this._dom.scrollLoader?.classList.toggle('hidden', !on);
    }

    // ── assortment add/remove events (from <assortment-interact>) ────────────────
    _attachEventListeners() {
      this._handlers.add = e => this._onAssortmentChange(e, 'add');
      this._handlers.remove = e => this._onAssortmentChange(e, 'remove');
      document.addEventListener('on:product-assortment:add', this._handlers.add);
      document.addEventListener('on:product-assortment:remove', this._handlers.remove);
      // Sidebar section collapse toggles.
      this._handlers.tagToggle = () => this._dom.tagFilter?.querySelector('ul')?.classList.toggle('visible');
      this._handlers.catToggle = () => this._dom.categoryFilter?.querySelector('ul')?.classList.toggle('visible');
      this._dom.tagFilter?.querySelector('h6')?.addEventListener('click', this._handlers.tagToggle);
      this._dom.categoryFilter?.querySelector('h6')?.addEventListener('click', this._handlers.catToggle);
      // Keep in-cart state in sync with the local cart.
      this._handlers.cartUpdated = () => {
        const previousDate = this._currentDate;
        this._loadCart();
        this._applyCartState();
        // A new delivery date points at a different `last_orders` bucket, so the
        // rendered badges no longer describe the selected date.
        if (this._lastOrderEnabled && this._currentDate !== previousDate) this._refreshLastOrderIndicators();
      };
      document.addEventListener('cart:updated', this._handlers.cartUpdated);
      // Custom-tag input: sanitise HTML and toggle the "+" button.
      this._handlers.createFilterInput = () => this._onCreateFilterInput();
      this._dom.createFilterInput?.addEventListener('input', this._handlers.createFilterInput);
      // Custom-tag "+" button: create the tag via set_assortment.
      this._handlers.createFilterClick = () => this._onCreateFilterClick();
      this._dom.createFilterButton?.addEventListener('click', this._handlers.createFilterClick);
      // Inline "Sortieren" input + OK (delegated — cards are added dynamically).
      this._handlers.sortClick = e => {
        const okBtn = e.target.closest('[data-sort-ok]');
        if (okBtn && this.contains(okBtn)) this._onSortInputSubmit(okBtn);
      };
      this.addEventListener('click', this._handlers.sortClick);
      // Keep the sort input digit-only (matches the old dialog's sanitiser).
      this._handlers.sortInput = e => {
        const input = e.target.closest('[data-sort-input]');
        if (!input || !this.contains(input)) return;
        const cleaned = input.value.replace(/[^\d]/g, '');
        if (cleaned !== input.value) input.value = cleaned;
        input.classList.remove('assortment-sort__input--error');
      };
      this.addEventListener('input', this._handlers.sortInput);
      // Enter inside the sort input submits, same as clicking OK.
      this._handlers.sortKeydown = e => {
        if (e.key !== 'Enter') return;
        const input = e.target.closest('[data-sort-input]');
        if (!input || !this.contains(input)) return;
        e.preventDefault();
        const okBtn = input.closest('[data-sort-action]')?.querySelector('[data-sort-ok]');
        if (okBtn) this._onSortInputSubmit(okBtn);
      };
      this.addEventListener('keydown', this._handlers.sortKeydown);
    }

    _detachEventListeners() {
      document.removeEventListener('on:product-assortment:add', this._handlers.add);
      document.removeEventListener('on:product-assortment:remove', this._handlers.remove);
      this._dom?.tagFilter?.querySelector('h6')?.removeEventListener('click', this._handlers.tagToggle);
      this._dom?.categoryFilter?.querySelector('h6')?.removeEventListener('click', this._handlers.catToggle);
      document.removeEventListener('cart:updated', this._handlers.cartUpdated);
      this._dom?.createFilterInput?.removeEventListener('input', this._handlers.createFilterInput);
      this._dom?.createFilterButton?.removeEventListener('click', this._handlers.createFilterClick);
      if (this._handlers.sortClick) this.removeEventListener('click', this._handlers.sortClick);
      if (this._handlers.sortInput) this.removeEventListener('input', this._handlers.sortInput);
      if (this._handlers.sortKeydown) this.removeEventListener('keydown', this._handlers.sortKeydown);
      if (this._handlers.resetSortClick) this._dom?.resetSortBtn?.removeEventListener('click', this._handlers.resetSortClick);
      this._sortDialog?.destroy();
      this._confirmDialog?.destroy();
      if (this._observer) this._observer.disconnect();
      if (this._handlers.scroll) window.removeEventListener('scroll', this._handlers.scroll);
    }

    /**
     * React to <assortment-interact> events.
     *   • tag present  → a single custom-tag was connected/disconnected for a
     *     product: update that tag's handles/count, sidebar, badges, per-card state.
     *   • no tag (remove) → the whole product left the KIOS: drop it from every
     *     custom tag, the KIOS list, memory + IndexedDB cache, refresh both
     *     sidebars, and remove its card from the DOM immediately.
     */
    _onAssortmentChange({ detail }, action) {
      const { handle, tag, productId } = detail ?? {};
      if (tag) {
        const t = this._customTags.find(ct => ct.name === tag);
        if (t) {
          if (action === 'add' && !t.handles.includes(handle)) t.handles.push(handle);
          if (action === 'remove') t.handles = t.handles.filter(h => h !== handle);
          t.count = t.handles.filter(h => h?.trim()).length;
          this._syncTagCountsToDOM();
          this._updateTagSidebarCount(t);
          this._updateBadge(handle, t.name, action);

          // Viewing exactly this tag and the product just lost it → it no longer
          // belongs in the filtered set. Drop its card now; if that empties the
          // view, leave the filter and restore the normal assortment view.
          const viewingThisTag = this._mode === 'filter'
            && this._activeFilter?.type === 'custom-tag'
            && this._activeFilter?.key === tag;
          if (action === 'remove' && viewingThisTag) {
            this._removeCardFromView(handle);
            if (!this._dom.products?.querySelector('.js-pagination-result')) {
              this._exitActiveFilter();
            }
          }
        }
        return;
      }
      if (action === 'remove' && (productId != null || handle)) {
        this._removeProduct(handle, productId);
      }
    }

    /**
     * Remove a product everywhere after it leaves the KIOS (bugs #1–#3).
     * @param {string} handle
     * @param {string|number} productId
     */
    _removeProduct(handle, productId) {
      // 1. Cache + in-memory.
      if (productId != null) {
        JsonCache.del(String(productId)).catch(() => {});
        this._products.delete(String(productId));
      }
      // 2. KIOS master list + index.
      this._kios = this._kios.filter(k => k.handle !== handle && String(k.productId) !== String(productId));
      this._buildKiosIndex();
      // 3. Custom tags: drop the handle, refresh sidebar counts + badges.
      for (const t of this._customTags) {
        if (!t.handles.includes(handle)) continue;
        t.handles = t.handles.filter(h => h !== handle);
        t.count = t.handles.filter(h => h?.trim()).length;
        this._updateTagSidebarCount(t);
      }
      // Push the new counts into every remaining card's `.assortment-custom-list`
      // so the per-card tag counts update instantly (no page refresh). The sidebar
      // was handled above; this covers the per-card `<li data-count>` displays.
      this._syncTagCountsToDOM();
      // 4. Category sidebar (counts may have dropped a category to zero).
      this._buildCategoriesFromKios();
      this._renderCategorySidebar();
      // 5. Remove the card from the DOM (available + unavailable grids).
      const sel = productId != null
        ? `.js-pagination-result[data-product-id="${CSS.escape(String(productId))}"]`
        : null;
      if (sel) this.querySelectorAll(sel).forEach(li => li.remove());
      if (handle) {
        this.querySelectorAll(`[data-handle="${CSS.escape(handle)}"]`).forEach(el => {
          el.closest('.js-pagination-result')?.remove();
        });
      }
      // 5b. Drop any category heading left with no products beneath it.
      this._pruneEmptyCategories();
      // 6. If a filter is active and now empty, fall back to normal browsing.
      if (this._activeFilter && !this._dom.products?.querySelector('.js-pagination-result')) {
        this._exitActiveFilter();
      }
    }

    /** Remove a product's card from the visible grid(s) by handle. */
    _removeCardFromView(handle) {
      if (!handle) return;
      this.querySelectorAll(`[data-handle="${CSS.escape(handle)}"]`).forEach(el => {
        el.closest('.js-pagination-result')?.remove();
      });
      this._pruneEmptyCategories();
    }

    /**
     * Drop any `.category--title` heading that no longer has product cards under
     * it. Headings and `.js-pagination-result` cards are siblings in the grid:
     * a heading "owns" every card between it and the next heading. Runs on both
     * the normal and active custom-tag views (both share `[data-products]`).
     *
     * Removing an emptied heading can leave two identical ones back to back — a
     * category interrupted by a single re-sorted product, whose card then leaves
     * the KIOS. The second is dropped too so the two runs merge back into one.
     */
    _pruneEmptyCategories() {
      const grid = this._dom.products;
      if (!grid) return;
      grid.querySelectorAll('.category--title').forEach(heading => {
        let el = heading.nextElementSibling;
        let hasProduct = false;
        while (el && !el.classList.contains('category--title')) {
          if (el.classList.contains('js-pagination-result')) { hasProduct = true; break; }
          el = el.nextElementSibling;
        }
        if (!hasProduct) heading.remove();
      });
      let previous = null;
      grid.querySelectorAll('.category--title').forEach(heading => {
        if (previous === heading.dataset.category) heading.remove();
        else previous = heading.dataset.category;
      });
    }

    /**
     * Leave the active filter and restore the normal assortment view (previous
     * scroll position + already-loaded products). Used both when a product
     * leaves the KIOS and when the active custom tag empties out.
     */
    _exitActiveFilter() {
      this.querySelectorAll('[data-filter-item]').forEach(el => el.classList.remove('active'));
      this._categories.forEach(c => (c.active = false));
      this._customTags.forEach(t => (t.active = false));
      this._activeFilter = null;
      this._switchToNormal();
    }

    /** Add/remove a custom-tag colour badge on the live card(s) for `handle`. */
    _updateBadge(handle, tagName, action) {
      const [label, color] = tagName.split(':');
      this.querySelectorAll(`.js-pagination-result [data-handle="${CSS.escape(handle)}"] [data-filter-badges]`).forEach(box => {
        const existing = box.querySelector(`[data-badge-filter="${CSS.escape(tagName)}"]`);
        if (action === 'add' && !existing) {
          const div = document.createElement('div');
          div.className = 'badge-item';
          div.dataset.badgeFilter = tagName;
          const span = document.createElement('span');
          span.style.backgroundColor = color ?? '';
          span.textContent = label ?? tagName;
          div.appendChild(span);
          box.appendChild(div);
        } else if (action === 'remove' && existing) {
          existing.remove();
        }
      });
    }

    _updateTagSidebarCount(tag) {
      const el = this._dom.tagFilterList?.querySelector(`[data-filter="${CSS.escape(tag.name)}"]`);
      if (!el) return;
      el.dataset.count = tag.count;
      const d = el.querySelector('[data-count-display]');
      if (d) d.textContent = addNumberPrefix(tag.count);
    }

    _syncTagCountsToDOM() {
      const map = new Map(this._customTags.map(t => [t.name, {
        count: t.handles.filter(h => h?.trim()).length,
        set: new Set(t.handles.filter(h => h?.trim())),
      }]));
      this.querySelectorAll('assortment-interact').forEach(interact => {
        const handle = interact.dataset.handle;
        interact.querySelectorAll('li[data-filter]').forEach(li => {
          const info = map.get(li.dataset.filter);
          if (!info) return;
          li.dataset.count = info.count;
          li.dataset.included = String(info.set.has(handle));
          const c = li.querySelector('[data-count]');
          if (c) c.textContent = info.count;
        });
      });
    }

    // ── manual sort ("Sortieren") ───────────────────────────────────────────────
    /**
     * Validate + submit the inline sort input when its OK button is clicked (or
     * Enter is pressed in the input). The action wrapper carries the same identity
     * CREATE_KIO uses (productId / variantId / handle); the value must be an
     * integer in [1, assortment size]. Invalid input flags the field; a failed save
     * re-flags it and re-enables OK. Re-confirming the position the product already
     * holds is a no-op — nothing moves, so nothing is sent.
     */
    async _onSortInputSubmit(okBtn) {
      const wrap = okBtn.closest('[data-sort-action]');
      if (!wrap) return;
      const input = wrap.querySelector('[data-sort-input]');
      const { productId, variantId, handle } = wrap.dataset;
      const max = this._sortMax();
      const raw = (input?.value ?? '').trim();
      const n = /^\d+$/.test(raw) ? Number(raw) : NaN;
      if (!(Number.isInteger(n) && n >= 1 && n <= max)) {
        input?.classList.add('assortment-sort__input--error');
        input?.setCustomValidity(`Position must be between 1 and ${max}`);
        input?.reportValidity();
        input?.focus();
        return;
      }
      input?.classList.remove('assortment-sort__input--error');
      input?.setCustomValidity(``);
      if (n === toSortIndex(wrap.dataset.sortIndex)) return;
      okBtn.classList.add('is-loading');
      okBtn.disabled = true;
      if (input) input.disabled = true;
      let ok = false;
      try { ok = await this._submitSort({ productId, variantId, handle }, n); } catch (e) { ok = false; }
      // On success the grid re-renders (this card is replaced), so only restore
      // state / flag the error when the save failed and the DOM is still intact.
      if (!ok) {
        okBtn.classList.remove('is-loading');
        okBtn.disabled = false;
        if (input) { input.disabled = false; input.classList.add('assortment-sort__input--error'); }
      }
    }

    /**
     * Normalise one `assortment_sorted_products` entry (or a KIOS-derived one) to
     * `{ kioId, variantId, productId, handle, sortOrder }` with numeric ids and a
     * positive-integer sortOrder (null when absent).
     */
    _normaliseSortedEntry(e) {
      return {
        kioId: e?.kioId ?? e?.id ?? null,
        variantId: stripVariantGid(e?.variantId),
        productId: stripProductGid(e?.productId),
        handle: e?.handle ?? '',
        sortOrder: toSortIndex(e?.sortOrder ?? e?.sortIndex),
      };
    }

    /**
     * Build `this._sortedProducts` — the ordered list of manually-sorted products,
     * the base the reshuffle mutates and the source of the full `sortIndexs`
     * payload. Prefers `configuration.assortment_products.assortment_sorted_products`
     * (also accepts a top-level `assortment_sorted_products`); otherwise derives it
     * from KIOS entries that already carry a sortIndex. When an explicit list is
     * present its orders are merged back onto the KIOS entries so load-time
     * ordering matches the authoritative source.
     */
    _buildSortedProducts() {
      const cfg = this.configuration ?? {};
      const ap = cfg.assortment_products;
      let raw = null;
      if (Array.isArray(cfg.assortment_sorted_products)) raw = cfg.assortment_sorted_products;
      else if (ap && !Array.isArray(ap) && Array.isArray(ap.assortment_sorted_products)) raw = ap.assortment_sorted_products;

      let list;
      if (raw) {
        list = raw.map(e => this._normaliseSortedEntry(e))
          .filter(e => e.sortOrder != null && (e.variantId || e.kioId != null || e.productId));
      } else {
        list = this._kios
          .filter(k => k.sortIndex != null)
          .map(k => ({ kioId: k.id, variantId: k.variantId, productId: k.productId, handle: k.handle, sortOrder: k.sortIndex }));
      }
      list.sort((a, b) => a.sortOrder - b.sortOrder);
      this._sortedProducts = list;

      if (raw) {
        const byVariant = new Map(list.filter(e => e.variantId).map(e => [String(e.variantId), e.sortOrder]));
        const byKio = new Map(list.filter(e => e.kioId != null).map(e => [String(e.kioId), e.sortOrder]));
        const byProduct = new Map(list.filter(e => e.productId).map(e => [String(e.productId), e.sortOrder]));
        for (const k of this._kios) {
          const so = byVariant.get(String(k.variantId)) ?? byKio.get(String(k.id)) ?? byProduct.get(String(k.productId));
          if (so != null) k.sortIndex = so;
        }
      }
    }

    /** True when `a` and `b` reference the same KIOS line (variant → kio → product). */
    _sameSorted(a, b) {
      if (a.variantId && b.variantId && String(a.variantId) === String(b.variantId)) return true;
      if (a.kioId != null && b.kioId != null && String(a.kioId) === String(b.kioId)) return true;
      if (a.productId && b.productId && String(a.productId) === String(b.productId)) return true;
      return false;
    }

    /**
     * Reorder the WHOLE assortment by moving `target` to position `desiredOrder`,
     * exactly like dragging a row in a list: the product is lifted out of the list
     * and dropped back in at the requested slot, and every product between its old
     * and new position shifts by one to keep the sequence unique and contiguous
     * (1..n). Products outside that span keep their position.
     *
     *   250 products, move #10 → #25:  #11…#25 shift up to #10…#24, #10 becomes
     *   #25, #26+ are untouched.
     *   Move #8 → #2:  #2…#7 shift down to #3…#8, #8 becomes #2, #9+ untouched.
     *
     * The base list is `this._kios` in its current display order. Two numberings
     * meet here, and the translation between them is the whole point:
     *   • `desiredOrder` is what the customer typed — a position in the MAIN GRID,
     *     which counts orderable products only (_buildSortPositions), because
     *     not-orderable ones are shown apart in `[data-sale-unavailable]`.
     *   • the returned `sortOrder`s number the FULL list 1..N, not-orderable
     *     entries included, because that is the order the backend persists.
     * So the moved entry is dropped in front of the `desiredOrder`-th orderable
     * product still standing (past the last one → the end of the list), and the
     * whole list is renumbered from there. Not-orderable entries are never
     * counted, so they neither consume a position nor move relative to the list.
     *
     * Each entry keeps its category/sub-category identity (it is carried on the
     * KIOS entry itself), so only positions change. Nothing is mutated; a fresh
     * array of `{kioId, variantId, productId, handle, sortOrder}` is returned, or
     * null when the target is not in the list.
     */
    _computeSortIndexes(target, desiredOrder) {
      const list = this._kios.map(k => ({
        kioId: k.id, variantId: k.variantId, productId: k.productId, handle: k.handle, sortOrder: null,
      }));
      // Availability flags, kept index-aligned with `list` through the splices.
      const orderable = this._kios.map(k => this._isOrderableKio(k));
      const from = list.findIndex(e => this._sameSorted(e, target));
      if (from === -1) return null;
      const [moved] = list.splice(from, 1);
      orderable.splice(from, 1);
      // Main-grid position → index in the full list: the slot of the n-th
      // orderable entry left, or the end of the list when there is no n-th.
      const wanted = Math.max(desiredOrder, 1);
      let to = list.length;
      let seen = 0;
      for (let i = 0; i < list.length; i++) {
        if (!orderable[i]) continue;
        if (++seen === wanted) { to = i; break; }
      }
      list.splice(to, 0, moved);
      list.forEach((e, i) => { e.sortOrder = i + 1; });
      return list;
    }

    /**
     * POST the whole reordered assortment to set_assortment_sort. Body:
     *   { customerId, userNumber, userName,
     *     sortIndexs: [{ kioId, variantId(GID), sortOrder }, …] }
     * The reshuffle is computed client-side over the full list (unique, contiguous
     * orders guaranteed) and, on success, applied live (no page reload) — the KIOS
     * list is re-sorted, `assortment_sorted_products` is updated, and the active
     * view re-renders.
     * @returns {Promise<boolean>} whether the change was persisted.
     */
    async _submitSort({ productId, variantId, handle }, desiredOrder) {
      const k = this._kiosByProductId.get(String(productId));
      const target = {
        kioId: k?.id ?? null,
        variantId: k?.variantId || stripVariantGid(variantId),
        productId: k?.productId || stripProductGid(productId),
        handle: k?.handle || handle || '',
      };
      const result = this._computeSortIndexes(target, desiredOrder);
      if (!result) {
        console.error('[MyAssortment] sort target not found in the assortment:', productId);
        return false;
      }
      const payload = {
        customerId: this.customer.id,
        userName: this.customer.userName,
        userNumber: this.customer.userNumber,
        sortIndexs: result.map(e => ({
          kioId: e.kioId,
          variantId: e.variantId ? `gid://shopify/ProductVariant/${e.variantId}` : null,
          sortOrder: e.sortOrder,
        })),
      };
      try {
        const res = await fetch(`${location.origin}${API.SET_ASSORTMENT_SORT}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // AS400-offline handling, mirroring create_kio/delete_kio.
        const json = await res.json().catch(() => null);
        if (json?.errors?.length && json.errors.join('').includes('server_offline')) {
          document.getElementById('serverOfflineModal')?.setAttribute('open', '');
          return false;
        }
        await this._applySortResult(result);
        if (this.enableLog) console.log(`[MyAssortment] set_assortment_sort → ${result.length} entries (target #${desiredOrder})`);
        return true;
      } catch (e) {
        console.error('[MyAssortment] set_assortment_sort failed:', e);
        return false;
      }
    }

    /**
     * Apply a persisted sort result live: replace `this._sortedProducts`, merge the
     * new orders onto the KIOS entries, re-sort + re-index them, and re-render the
     * active view (normal or filtered) in place — no page reload.
     */
    async _applySortResult(result) {
      this._sortedProducts = result.map(e => ({ ...e }));
      const byVariant = new Map(result.filter(e => e.variantId).map(e => [String(e.variantId), e.sortOrder]));
      const byKio = new Map(result.filter(e => e.kioId != null).map(e => [String(e.kioId), e.sortOrder]));
      const byProduct = new Map(result.filter(e => e.productId).map(e => [String(e.productId), e.sortOrder]));
      for (const k of this._kios) {
        const so = byVariant.get(String(k.variantId)) ?? byKio.get(String(k.id)) ?? byProduct.get(String(k.productId));
        if (so != null) k.sortIndex = so;
      }
      this._hasSortIndex = this._kios.some(k => k.sortIndex != null);
      this._sortKios();
      this._buildKiosIndex();
      this._updateResetSortButton();
      await this._rerenderCurrentView();
    }

    /**
     * Re-render whichever view is active in the new order, fetching only products
     * not already cached. Guarded against overlapping with infinite scroll.
     */
    async _rerenderCurrentView() {
      if (this._isFetching) return;
      this._isFetching = true;
      this._setScrollLoader(true);
      const scrollY = window.scrollY;
      try {
        const entries = (this._mode === 'filter' && this._activeFilter)
          ? this._filterEntries(this._activeFilter)
          : this._kios.slice(0, Math.max(this._currentPage, 1) * PAGE_SIZE);
        await this._ensureProductsLoaded(entries);
        this._clearGrid();
        if (this._mode !== 'filter') this._mode = 'normal';
        if (entries.length) this._appendCards(entries);
      } catch (e) {
        console.error('[MyAssortment] re-render after sort failed:', e);
      } finally {
        this._setScrollLoader(false);
        this._isFetching = false;
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
      }
    }

    /** KIOS entries matching an active filter descriptor, in current sort order. */
    _filterEntries({ type, key }) {
      if (type === 'category') return this._kios.filter(k => catList(k.categories).includes(key));
      const tag = this._customTags.find(t => t.name === key);
      const set = new Set(tag?.handles ?? []);
      return this._kios.filter(k => set.has(k.handle));
    }

    /**
     * Ensure every product in `slice` is in memory: check memory, then the IDB
     * cache by product id, then fetch only the still-missing ones by KIOS id.
     */
    async _ensureProductsLoaded(slice) {
      let need = slice.filter(k => !this._products.has(String(k.productId)));
      if (need.length && !cacheDisabled()) {
        const cached = await JsonCache.getByProductIds(need.map(k => k.productId).filter(Boolean));
        for (const [pid, data] of cached) this._products.set(String(pid), data);
      }
      const missing = slice.filter(k => !this._products.has(String(k.productId)));
      if (missing.length) {
        const fetched = await this._postDetails({ page: 1, per_page: Math.max(missing.length, PAGE_SIZE), kioIds: missing.map(k => k.id).filter(v => v != null) });
        this._cacheFetched(fetched);
      }
    }

    /**
     * Inject (once) the scoped styles shared by the inline sort field and the
     * reset-confirmation dialog: the field wraps the positioned sort icon over a
     * left-padded input, and the dialog styles back _ensureConfirmDialog.
     */
    _injectSortStyles() {
      if (document.getElementById('assortment-sort-styles')) return;
      const style = document.createElement('style');
      style.id = 'assortment-sort-styles';
      style.textContent = `
        .assortment-sort-dialog{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center}
        .assortment-sort-dialog[hidden]{display:none}
        .assortment-sort-dialog__overlay{position:absolute;inset:0;background:rgba(0,0,0,.5)}
        .assortment-sort-dialog__window{position:relative;width:min(420px,calc(100vw - 40px));background:#fff;color:#3c0037;border-radius:10px;padding:24px;box-shadow:0 20px 60px rgba(0,0,0,.25);animation:assortment-sort-in .15s ease-out}
        @keyframes assortment-sort-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`;
      document.head.appendChild(style);
    }

    // ── reset sorting ────────────────────────────────────────────────────────────
    /**
     * Create the "Sortierung zurücksetzen" button next to the custom-tag filter.
     * It is only shown while a manual sort is applied (assortment_sorted_products
     * non-empty) and toggled by _updateResetSortButton whenever that changes.
     */
    _renderResetSortButton() {
      if (!this._enableCustomSort) return;
      if (!this._dom.tagFilter || this._dom.resetSortBtn) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn--primary assortment-reset-sort';
      btn.setAttribute('data-reset-sort', '');
      btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" stroke="currentColor" stroke-width="20" width="18px" height="18px" viewBox="0 0 512 512" aria-hidden="true" focusable="false"><g><path d="M64,256H34A222,222,0,0,1,430,118.15V85h30V190H355V160h67.27A192.21,192.21,0,0,0,256,64C150.13,64,64,150.13,64,256Zm384,0c0,105.87-86.13,192-192,192A192.21,192.21,0,0,1,89.73,352H157V322H52V427H82V393.85A222,222,0,0,0,478,256Z"/></g></svg><span>Sortierung zurücksetzen</span>`;
      this._dom.tagFilter.insertAdjacentElement('afterend', btn);
      this._dom.resetSortBtn = btn;
      this._handlers.resetSortClick = () => this._onResetSortClick();
      btn.addEventListener('click', this._handlers.resetSortClick);
      this._updateResetSortButton();
    }

    /** Show the reset button only when a manual sort is currently applied. */
    _updateResetSortButton() {
      const btn = this._dom?.resetSortBtn;
      if (!btn) return;
      btn.style.display = this._sortedProducts.length > 0 ? '' : 'none';
    }

    /** Confirm, then clear every sortOrder. */
    _onResetSortClick() {
      if (!this._sortedProducts.length) return;
      this._ensureConfirmDialog().open({
        opener: this._dom.resetSortBtn,
        onConfirm: () => this._submitReset(),
      });
    }

    /**
     * POST the full sorted list with `sortOrder: null` for every entry to
     * set_assortment_sort, clearing the manual order server-side. On success the
     * order is dropped locally and the view re-renders in the default order.
     * @returns {Promise<boolean>}
     */
    async _submitReset() {
      // Every currently-sorted object, sourced from live KIOS state (each entry's
      // sortIndex) unioned with _sortedProducts (covers orphans no longer in KIOS),
      // deduped by variant/kio. Each is sent with sortOrder EXPLICITLY null so the
      // backend clears the manual order — never the previous numeric value.
      const byKey = new Map();
      const keyOf = e => `${e.variantId ?? ''}|${e.kioId ?? ''}`;
      for (const e of this._sortedProducts) {
        byKey.set(keyOf(e), { kioId: e.kioId ?? null, variantId: e.variantId ?? null });
      }
      for (const k of this._kios) {
        if (k.sortIndex == null) continue;
        const e = { kioId: k.id ?? null, variantId: k.variantId ?? null };
        byKey.set(keyOf(e), e);
      }
      const payload = {
        customerId: this.customer.id,
        userName: this.customer.userName,
        userNumber: this.customer.userNumber,
        sortIndexs: [...byKey.values()].map(e => ({
          kioId: e.kioId,
          variantId: e.variantId ? `gid://shopify/ProductVariant/${e.variantId}` : null,
          sortOrder: -1,
        })),
      };
      try {
        const res = await fetch(`${location.origin}${API.SET_ASSORTMENT_SORT}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json().catch(() => null);
        if (json?.errors?.length && json.errors.join('').includes('server_offline')) {
          document.getElementById('serverOfflineModal')?.setAttribute('open', '');
          return false;
        }
        //await this._applyReset();
        if (this.enableLog) console.log('[MyAssortment] sort reset');
        location.reload();
        return true;
      } catch (e) {
        console.error('[MyAssortment] reset sort failed:', e);
        return false;
      }
    }

    /** Drop every manual sortIndex, revert to default ordering, and re-render live. */
    async _applyReset() {
      for (const k of this._kios) k.sortIndex = null;
      this._sortedProducts = [];
      this._hasSortIndex = false;
      this._sortKios();
      this._buildKiosIndex();
      this._updateResetSortButton();
      await this._rerenderCurrentView();
    }

    /**
     * Lazily build (once) a shared Cancel/OK confirmation dialog, reusing the sort
     * dialog's styling. `open({ onConfirm })` runs the async confirm on OK and
     * closes on success; Cancel/overlay/Escape close with no action.
     */
    _ensureConfirmDialog() {
      if (this._confirmDialog) return this._confirmDialog;
      this._injectSortStyles(); // ensures the shared dialog styles are injected

      const root = document.createElement('div');
      root.className = 'assortment-sort-dialog';
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.hidden = true;
      root.innerHTML = `
        <div class="assortment-sort-dialog__overlay" data-overlay></div>
        <div class="assortment-sort-dialog__window" role="document">
          <h6 class="h6">Möchten Sie die Sortierung wirklich zurücksetzen?</h6>
          <p>Die manuelle Produktsortierung wird entfernt und die Standardsortierung wiederhergestellt.</p>
          <p class="text-sm color-red" data-error hidden></p>
          <div class="flex gap-4 justify-end mt-6">
            <button type="button" class="btn btn--secondary" data-cancel>Abbrechen</button>
            <button type="button" class="btn btn--primary" data-ok>OK</button>
          </div>
        </div>`;
      document.body.appendChild(root);

      const okBtn = root.querySelector('[data-ok]');
      const cancelBtn = root.querySelector('[data-cancel]');
      const overlay = root.querySelector('[data-overlay]');
      const errorEl = root.querySelector('[data-error]');
      const state = { onConfirm: null, opener: null, submitting: false };
      const showError = msg => { errorEl.textContent = msg ?? ''; errorEl.hidden = !msg; };

      const close = () => {
        if (state.submitting) return;
        root.hidden = true;
        document.removeEventListener('keydown', onKeydown, true);
        const opener = state.opener;
        state.onConfirm = null;
        state.opener = null;
        if (opener && opener.isConnected) opener.focus?.();
      };

      const onKeydown = e => {
        if (root.hidden) return;
        if (e.key === 'Escape') { e.preventDefault(); close(); return; }
        if (e.key === 'Tab') {
          const focusables = [cancelBtn, okBtn].filter(el => !el.disabled);
          if (!focusables.length) return;
          const first = focusables[0], last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };

      cancelBtn.addEventListener('click', close);
      overlay.addEventListener('click', close);
      okBtn.addEventListener('click', async () => {
        if (state.submitting) return;
        state.submitting = true;
        okBtn.classList.add('is-loading');
        okBtn.disabled = true;
        cancelBtn.disabled = true;
        let ok = false;
        try { ok = await state.onConfirm?.(); } catch (e) { ok = false; }
        state.submitting = false;
        okBtn.classList.remove('is-loading');
        okBtn.disabled = false;
        cancelBtn.disabled = false;
        if (ok) close();
        else showError('Die Aktion konnte nicht ausgeführt werden. Bitte versuchen Sie es erneut.');
      });

      this._confirmDialog = {
        open: ({ onConfirm, opener }) => {
          state.onConfirm = onConfirm;
          state.opener = opener ?? null;
          state.submitting = false;
          showError('');
          okBtn.disabled = false;
          cancelBtn.disabled = false;
          root.hidden = false;
          requestAnimationFrame(() => {
            cancelBtn.focus();
            document.addEventListener('keydown', onKeydown, true);
          });
        },
        close,
        destroy: () => {
          document.removeEventListener('keydown', onKeydown, true);
          root.remove();
          this._confirmDialog = null;
        },
      };
      return this._confirmDialog;
    }

    _applyTabIndexes() {
      this.querySelectorAll('.qty-input button, assortment-interact button, .card-link, .js-option, custom-select button, [data-sort-input], [data-sort-ok]')
        .forEach(el => el.setAttribute('tabindex', '-1'));
    }
  }

  customElements.define('my-assortment', MyAssortment);
})();
